import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../axios_helper';
import { persistStoredUser } from '../utils/userStorage';
import { COUNTRY_PT, CITY_PT } from '../utils/localization';
import { EN_UI_TRANSLATIONS, PT_TO_LOCALE, SUPPORTED_LANGUAGE_CODES } from './localeTranslations';

/** Resolve a browser/IME locale to one of the interface languages we ship. */
export function resolveSupportedLanguage(locale) {
  if (typeof locale !== 'string' || !locale.trim()) return null;
  const language = locale.trim().replace(/_/g, '-').split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGE_CODES.includes(language) ? language : null;
}

export function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return null;
  const preferences = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  return preferences.map(resolveSupportedLanguage).find(Boolean) || null;
}

export const MANUAL_LANGUAGE_STORAGE_KEY = 'gm:language:manual';

/** Manual device choice > browser preference > previous local value > account fallback. */
export function resolveInitialLanguage({ manualLanguage, browserLanguage, storedLanguage, accountLanguage } = {}) {
  return [manualLanguage, browserLanguage, storedLanguage, accountLanguage]
    .map(resolveSupportedLanguage)
    .find(Boolean) || 'pt';
}

const readLocalLanguage = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    return resolveSupportedLanguage(window.localStorage.getItem(key));
  } catch (_) {
    return null;
  }
};

const isNativeAndroid = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

/**
 * The interface was originally written in Portuguese.  The language layer is
 * deliberately kept local to the frontend so it also covers lazy-loaded pages
 * and text rendered by older components that do not consume a translation
 * hook yet.  User supplied content is not translated: only known interface
 * phrases are mapped here.
 */
const PT_TO_EN = {
  'Início': 'Home',
  'Descobrir': 'Discover',
  'Mapa': 'Map',
  'Meteorologia': 'Weather',
  'Fórum': 'Forum',
  'Notificações': 'Notifications',
  'notificações': 'notifications',
  'Feedback e Sugestões': 'Feedback & Suggestions',
  'Definições': 'Settings',
  'Pesquisar': 'Search',
  'Procurar': 'Search',
  'Entrar': 'Log in',
  'Criar conta': 'Create account',
  'Registar': 'Sign up',
  'Sair': 'Log out',
  'Terminar sessão': 'Log out',
  'Terminar Sessão em Todo o Lado': 'Log out everywhere',
  'Perfil': 'Profile',
  'Viajante': 'Traveller',
  'Viajantes': 'Travellers',
  'Viagem': 'Trip',
  'Viagens': 'Trips',
  'viagem': 'trip',
  'viagens': 'trips',
  'Estatísticas': 'Statistics',
  'estatísticas': 'statistics',
  'Seguidores': 'Followers',
  'seguidores': 'followers',
  'A seguir': 'Following',
  'a seguir': 'following',
  'Gostos': 'Likes',
  'gostos': 'likes',
  'Comentários': 'Comments',
  'comentários': 'comments',
  'Com quem viajei': 'Travel companions',
  'Partilhar': 'Share',
  'Partilhar perfil': 'Share profile',
  'Partilhar ligação': 'Share link',
  'Partilhar imagem': 'Share image',
  'Partilhar viagens': 'Share trips',
  'Guardar': 'Save',
  'Guardar viagem': 'Save trip',
  'Guardado': 'Saved',
  'Remover dos guardados': 'Remove from saved',
  'Editar': 'Edit',
  'Editar perfil': 'Edit profile',
  'Editar viagem': 'Edit trip',
  'Eliminar': 'Delete',
  'Remover': 'Remove',
  'Adicionar': 'Add',
  'Adicionar atividade': 'Add activity',
  'Cancelar': 'Cancel',
  'Confirmar': 'Confirm',
  'Fechar': 'Close',
  'Voltar': 'Back',
  'Anterior': 'Previous',
  'Seguinte': 'Next',
  'Continuar': 'Continue',
  'Concluir': 'Finish',
  'Ver mais': 'See more',
  'Ler mais': 'Read more',
  'Tentar novamente': 'Try again',
  'Carregar': 'Upload',
  'Descarregar': 'Download',
  'Atualizar': 'Refresh',
  'Atualizar Viagem': 'Update trip',
  'Guardar Alterações': 'Save changes',
  'A guardar…': 'Saving…',
  'A carregar…': 'Loading…',
  'A entrar…': 'Logging in…',
  'A sair…': 'Logging out…',
  'A abrir…': 'Opening…',
  'Em breve': 'Coming soon',
  'Público': 'Public',
  'Pública': 'Public',
  'Privado': 'Private',
  'Privada': 'Private',
  'Idioma': 'Language',
  'Idioma da interface': 'Interface language',
  'Português (PT)': 'Portuguese (PT)',
  'Inglês': 'English',
  'Espanhol': 'Spanish',
  'Español (Em breve)': 'Spanish (Coming soon)',
  'Definições e privacidade': 'Settings and privacy',
  'Privacidade': 'Privacy',
  'Atividade da Conta': 'Account activity',
  'Dispositivos ligados e sessões': 'Connected devices and sessions',
  'Ver Histórico': 'View history',
  'Permitir notificações push': 'Allow push notifications',
  'Notificações push': 'Push notifications',
  'Termos e Política': 'Terms and policy',
  'Termos': 'Terms',
  'Política de Privacidade': 'Privacy policy',
  'Dados da Conta': 'Account data',
  'Transferir': 'Export',
  'Mostrar para Todos': 'Show to everyone',
  'Qualquer pessoa vê': 'Anyone can see it',
  'Apenas Seguidores': 'Followers only',
  'Só quem te segue': 'Only people who follow you',
  'Apenas para Mim': 'Only me',
  'Privado para ti': 'Private to you',
  'Destinos': 'Destinations',
  'Info': 'Info',
  'Datas e custos': 'Dates & costs',
  'Experiência': 'Experience',
  'Galeria': 'Gallery',
  'Companheiros': 'Companions',
  'Informações': 'Information',
  'Alojamento': 'Accommodation',
  'Alimentação': 'Food',
  'Transportes': 'Transport',
  'Locais': 'Places',
  'Itinerário': 'Itinerary',
  'Extras': 'Extras',
  'Categorias': 'Categories',
  'Idiomas': 'Languages',
  'Resumo': 'Summary',
  'Sobre': 'About',
  'Sobre esta viagem': 'About this trip',
  'Sobre o viajante': 'About the traveller',
  'Resumo da viagem': 'Trip summary',
  'Melhores destinos': 'Top destinations',
  'Top países': 'Top countries',
  'Top cidades': 'Top cities',
  'Mapa das viagens': 'Trip map',
  'Estatísticas gerais': 'General statistics',
  'Total de viagens': 'Total trips',
  'Países visitados': 'Countries visited',
  'Cidades visitadas': 'Cities visited',
  'Total gasto': 'Total spent',
  'Média por viagem': 'Average per trip',
  'Duração média': 'Average duration',
  'Ainda sem viagens': 'No trips yet',
  'Este viajante ainda não partilhou viagens.': 'This traveller has not shared any trips yet.',
  'Viajante não encontrado': 'Traveller not found',
  'Viagem não encontrada': 'Trip not found',
  'Sem resultados': 'No results',
  'Não foi possível carregar o perfil.': 'Could not load the profile.',
  'Não foi possível carregar as viagens.': 'Could not load the trips.',
  'Não foi possível carregar as viagens. Tente novamente.': 'Could not load the trips. Try again.',
  'Não foi possível guardar a viagem.': 'Could not save the trip.',
  'Não foi possível partilhar a imagem.': 'Could not share the image.',
  'Erro ao carregar as viagens. Tente novamente.': 'Error loading trips. Try again.',
  'Erro ao alterar idioma.': 'Error changing language.',
  'Idioma atualizado.': 'Language updated.',
  'Privacidade atualizada.': 'Privacy updated.',
  'Definições guardadas.': 'Settings saved.',
  'Não foi possível atualizar as notificações.': 'Could not update notifications.',
  'Não foi possível carregar o histórico de sessões.': 'Could not load session history.',
  'Sessão terminada.': 'Session ended.',
  'Ver comentários': 'View comments',
  'Carregar mais comentários': 'Load more comments',
  'Sem comentários': 'No comments',
  'Publicar': 'Publish',
  'Denunciar viagem': 'Report trip',
  'Denunciar viajante': 'Report traveller',
  'Seguir': 'Follow',
  'Deixar de seguir': 'Unfollow',
  'Bloquear': 'Block',
  'Desbloquear': 'Unblock',
  'Aceitar': 'Accept',
  'Rejeitar': 'Reject',
  'Notificação': 'Notification',
  'Ajuda e suporte': 'Help & support',
  'Ajuda': 'Help',
  'Pesquisar viagens…': 'Search trips…',
  'Pesquisar cidade': 'Search city',
  'Pesquisar cidade (ex.: Torres Vedras)': 'Search city (e.g. Torres Vedras)',
  'Pesquisar país, cidade...': 'Search country, city…',
  'Pesquisar por nome ou username...': 'Search by name or username…',
  'Selecione uma ou mais línguas': 'Select one or more languages',
  'Selecione uma ou mais categorias': 'Select one or more categories',
  'A carregar línguas…': 'Loading languages…',
  'A carregar categorias…': 'Loading categories…',
  'Escolher imagem': 'Choose image',
  'Imagem de capa': 'Cover image',
  'Fotografia de perfil': 'Profile photo',
  'Estilo de viagem preferido': 'Preferred travel style',
  'Natureza': 'Nature',
  'Praia': 'Beach',
  'Romântico': 'Romantic',
  'Aventura': 'Adventure',
  'Bem-estar': 'Wellness',
  'Campismo': 'Camping',
  'Cidade': 'City',
  'Compras': 'Shopping',
  'Cruzeiros': 'Cruises',
  'Cultural': 'Cultural',
  'Desporto': 'Sports',
  'Económico': 'Budget',
  'Espiritual': 'Spiritual',
  'Família': 'Family',
  'Fotografia': 'Photography',
  'Gastronomia': 'Food & wine',
  'Histórico': 'Historical',
  'Luxo': 'Luxury',
  'Montanha': 'Mountain',
  'Pontos de Interesse': 'Points of interest',
  'Como posso criar uma conta?': 'How can I create an account?',
  'Como editar e personalizar o meu perfil?': 'How do I edit and customise my profile?',
  'Como criar e registar uma nova viagem?': 'How do I create and record a new trip?',
  'Como partilhar o meu perfil com outros viajantes?': 'How do I share my profile with other travellers?',
  'Como planear viagens futuras?': 'How do I plan future trips?',
  'Ajuda e Suporte': 'Help & Support',
  'Estamos aqui para o ajudar a tirar o máximo partido da Globe Memories.': 'We are here to help you get the most out of Globe Memories.',
  'Perguntas Frequentes': 'Frequently Asked Questions',
  'respostas': 'answers',
  'Fala Connosco': 'Contact us',
  'Não encontraste resposta nas FAQ? Envia-nos uma mensagem detalhada. A mensagem fica disponível no backoffice para a equipa responder em 24 a 48 horas. Questões técnicas:': 'Did not find an answer in the FAQ? Send us a detailed message. The team will reply within 24 to 48 hours. Technical questions:',
  'Nome': 'Name',
  'O seu nome completo...': 'Your full name…',
  'O seu email de contacto...': 'Your contact email…',
  'Mensagem': 'Message',
  'A enviar...': 'Sending…',
  'Enviar Mensagem': 'Send Message',
  'Mensagem deve ter pelo menos 10 caracteres': 'Message must be at least 10 characters',
  'Nome deve ter pelo menos 2 caracteres': 'Name must be at least 2 characters',
  'Nome deve conter apenas letras': 'Name may only contain letters',
  'Pedido de ajuda e suporte': 'Help and support request',
  'Nova Pergunta': 'New Question',
  'Descreva a sua pergunta de forma clara e detalhada...': 'Describe your question clearly and in detail…',
  'Visibilidade da pergunta': 'Question visibility',
  'Perguntas visíveis e interativas apenas para seguidores do autor.': 'Questions are visible and interactive only for the author’s followers.',
  'Comece a interagir com a comunidade. Faça a sua primeira pergunta!': 'Start interacting with the community. Ask your first question!',
  'Viagens futuras': 'Future trips',
  'As minhas viagens': 'My trips',
  'Viagens Guardadas': 'Saved trips',
  'Explorar viajantes': 'Explore travellers',
  'Explorar Viagens': 'Explore trips',
  'Bem-vindo de volta. Inicia sessão para continuar.': 'Welcome back. Log in to continue.',
  'Continua a explorar': 'Keep exploring',
  'Descobre mais viagens, guarda experiências e interage com a comunidade.': 'Discover more trips, save experiences and connect with the community.',
  'Descobre experiências reais, compara percursos e encontra inspiração de quem já esteve lá.': 'Discover real experiences, compare routes and find inspiration from people who have been there.',
  'Partilha as tuas viagens.': 'Share your trips.',
  'A tua experiência pode tornar a viagem de alguém melhor.': 'Your experience can make someone else’s trip better.',
  'Experiências reais que ajudam a viajar melhor.': 'Real experiences that help people travel better.',
  'Descobre destinos e ideias através de viagens que aconteceram de verdade.': 'Discover destinations and ideas through trips that really happened.',
  'Globe Memories — início': 'Globe Memories — home',
  'Navegação principal': 'Main navigation',
  'Navegação primária': 'Primary navigation',
  'Navegação inferior': 'Bottom navigation',
  'Navegação': 'Navigation',
  'Pessoal': 'Personal',
  'Acesso': 'Access',
  'Convidado': 'Guest',
  'Conta': 'Account',
  'Regista quando acontece e quanto custou': 'Record when it happened and what it cost',
  'Quando aconteceu e quanto gastaste. Detalha voo, alojamento, comida e extras.': 'When it happened and what you spent. Add flights, accommodation, food and extras.',
  'Para onde foste? Adiciona cidades, vilas, parques, praias ou qualquer outro local.': 'Where did you go? Add cities, villages, parks, beaches or any other place.',
  'Categorias, idiomas utilizados na viagem, alojamento, alimentação, transporte, itinerário, extras.': 'Categories, languages used on the trip, accommodation, food, transport, itinerary and extras.',
  'Adiciona as tuas fotografias e vídeos e associa-os aos locais ou serviços da viagem.': 'Add your photos and videos and link them to the trip locations or services.',
  'Rever e publicar': 'Review & publish',
  'Publicar Viagem': 'Publish trip',
  'Atualizar viagem': 'Update trip',
  'Atualizar viagem…': 'Updating trip…',
  'A publicar viagem…': 'Publishing trip…',
  'A guardar a viagem atualizada…': 'Saving updated trip…',
  'A publicar a viagem…': 'Publishing trip…',
  'Para onde foste?': 'Where did you go?',
  'Um destino': 'One destination',
  'Vários destinos': 'Multiple destinations',
  'Adicionar destino': 'Add destination',
  'Adicionar local no mesmo país': 'Add a place in the same country',
  'Destino / local': 'Destination / place',
  'Destino ou local': 'Destination or place',
  'Escreve pelo menos 2 caracteres para adicionar este local': 'Type at least 2 characters to add this place',
  'Adicionaste': 'You added',
  'destino': 'destination',
  'destinos': 'destinations',
  'Onde foi esta viagem? Pode ser uma cidade, vila, parque, praia, montanha ou outro local.': 'Where was this trip? It can be a city, village, park, beach, mountain or another place.',
  'A experiência': 'The experience',
  'Secções da experiência': 'Experience sections',
  'Escolhe o que descreve melhor a viagem.': 'Choose what best describes the trip.',
  'Pratos que provaste e recomendas (opcional). Se adicionares uma recomendação, indica o nome e a descrição.': 'Dishes you tried and recommend (optional). If you add a recommendation, include its name and description.',
  'Organiza as atividades por dia e, nas viagens com vários destinos, associa cada dia ao local certo.': 'Organise activities by day and, on multi-destination trips, link each day to the right place.',
  'Destinos da viagem': 'Trip destinations',
  'Escolhe um destino para adicionar ou consultar as atividades desse local.': 'Choose a destination to add or view activities for that place.',
  'Nenhuma atividade adicionada': 'No activities added',
  'Clica em "Adicionar atividade" para preencher este dia.': 'Click “Add activity” to fill in this day.',
  'Guardar alteração': 'Save change',
  'Ao longo do percurso (sem destino específico)': 'Along the route (no specific destination)',
  'Destinos associados': 'Associated destinations',
  'Escolher tipo': 'Choose type',
  'Adicionar alojamento': 'Add accommodation',
  'Adicionar recomendação': 'Add recommendation',
  'Adicionar transporte': 'Add transport',
  'Adicionar ponto': 'Add point',
  'Adicionar ponto negativo': 'Add negative point',
  'Adicionar ponto positivo': 'Add positive point',
  'Adicionado': 'Added',
  'Ainda não adicionaste ninguém. Este passo é opcional.': 'You have not added anyone yet. This step is optional.',
  'Quem viajou contigo?': 'Who travelled with you?',
  'Viajantes identificados': 'Identified travellers',
  'Pesquisar por nome ou @utilizador': 'Search by name or @username',
  'Arrasta ficheiros ou escolhe do dispositivo': 'Drag files or choose from your device',
  'Limite de ficheiros atingido': 'File limit reached',
  'Escolher fotografias': 'Choose photos',
  'Adicionar vídeo': 'Add video',
  'Predefinição baseada no teu perfil. Podes alterar antes de publicar.': 'Default based on your profile. You can change it before publishing.',
  'Mapa interativo disponível após publicar': 'Interactive map available after publishing',
  'Ir para a app': 'Go to the app',
  'começou a seguir-te': 'started following you',
  'pediu para te seguir': 'requested to follow you',
  'aceitou o teu pedido para seguir': 'accepted your follow request',
  'Ocultar confirmação': 'Hide confirmation',
  'Pesquisa global': 'Global search',
  'Destino surpresa': 'Surprise destination',
  'Clique em "Destino surpresa" e descubra um destino aleatório.': 'Click “Surprise destination” and discover a random destination.',
  'Street View': 'Street View',
  'Atlas dark aurora': 'Dark aurora atlas',
  'Começar a explorar': 'Start exploring',
  'Públicas': 'Public',
  'Tente ajustar os filtros ou a pesquisa.': 'Try adjusting the filters or search.',
  'Pesquisar por nome ou utilizador': 'Search by name or username',
  'A pesquisar…': 'Searching…',
  'Pesquisa os viajantes que fizeram esta viagem contigo. Cada pessoa recebe um pedido e decide se quer aceitar; ao aceitar, é criada uma cópia editável no seu perfil.': 'Search for travellers who made this trip with you. Each person receives a request and decides whether to accept; once accepted, an editable copy is created on their profile.',
  // Trip details, media and tutorial copy
  'Abrir foto': 'Open photo',
  'Ponto de Interesse': 'Point of interest',
  'Roteiro da viagem': 'Trip route',
  'Detalhes da viagem': 'Trip details',
  'A carregar mapa': 'Loading map',
  'Adiciona uma foto de capa para ver aqui': 'Add a cover photo to see it here',
  'Ver perfil': 'View profile',
  'Mostra o estilo e o idioma da viagem': 'Show the trip style and language',
  'Seleciona as categorias que melhor descrevem a viagem.': 'Select the categories that best describe the trip.',
  'Regista o nome e o tipo de alojamento.': 'Record the accommodation name and type.',
  'Recomenda sabores e lugares para comer': 'Recommend flavours and places to eat',
  'Adiciona o nome do restaurante e uma nota para orientar outros viajantes.': 'Add the restaurant name and a note to guide other travellers.',
  'Carrega os melhores momentos e explica o que cada imagem mostra.': 'Upload your best moments and explain what each image shows.',
  'e descubra um destino aleatório.': 'and discover a random destination.',
  'Veja qualquer ponto do mapa em 360° antes de viajar.': 'View any point on the map in 360° before travelling.',
  'Paragem da viagem': 'Trip stop',
  'Erro ao carregar o feed.': 'Error loading feed.',
  'Link copiado.': 'Link copied.',
  'Removida dos guardados.': 'Removed from saved.',
  'Inicie sessão para dar gosto.': 'Log in to like this.',
  'Viagem guardada.': 'Trip saved.',
  'Inicie sessão para comentar.': 'Log in to comment.',
  'Solte para guardar': 'Release to save',
  'locais inexistentes, preços manipulados': 'non-existent places or manipulated prices',
  'Spam / autopromoção': 'Spam / self-promotion',
  'Plágio de conteúdo': 'Content plagiarism',
  'fotos ou textos copiados sem créditos': 'photos or text copied without credit',
  'Violação das regras': 'Rule violation',
  'imagens, descrições ou linguagem ofensiva': 'offensive images, descriptions or language',
  'perfil, viagens ou locais inventados': 'invented profiles, trips or places',
  'Assédio ou abuso': 'Harassment or abuse',
  'comportamento agressivo ou ameaçador': 'aggressive or threatening behaviour',
  'ameaças, doxxing ou intimidação': 'threats, doxxing or intimidation',
  'Reportado a partir dos detalhes da viagem.': 'Reported from the trip details.',
  'Utilizador denunciado.': 'User reported.',
  'Viagem denunciada. Obrigado.': 'Trip reported. Thank you.',
  'Denunciar utilizador': 'Report user',
  'Pausar vídeo': 'Pause video',
  'Silenciar vídeo': 'Mute video',
  'Mapa Mundo': 'World map',
  'Pesquisar viagens, pontos, itinerários ou viajantes...': 'Search trips, places, itineraries or travellers…',
  'Pesquisar viagens, pontos de referência, itinerários ou viajantes...': 'Search trips, points of interest, itineraries or travellers…',
  'Pesquisa viagens, lugares, pontos de referência, itinerários, categorias e viajantes em toda a Globe Memories.': 'Search trips, places, points of interest, itineraries, categories and travellers across Globe Memories.',
  'Ver detalhes da viagem': 'View trip details',
  'duração desconhecida': 'unknown duration',
  'Imagem de capa da viagem': 'Trip cover image',
  'Comida da viagem': 'Trip food',
  'O servidor não iniciou o upload por partes.': 'The server did not start the multipart upload.',
  'Não foi possível enviar uma parte do ficheiro.': 'Could not upload part of the file.',
  'A preparar a viagem…': 'Preparing the trip…',
  'Viagem atualizada com sucesso.': 'Trip updated successfully.',
  'Viagem publicada com sucesso.': 'Trip published successfully.',
  'Viagem atualizada!': 'Trip updated!',
  'Viagem publicada!': 'Trip published!',
  'A atualizar viagem…': 'Updating trip…',
  'A editar viagem': 'Editing trip',
  'Mín. 20 caracteres.': 'Min. 20 characters.',
  'Toca para avaliar': 'Tap to rate',
  '🗺️ Vários destinos': '🗺️ Multiple destinations',
  'Pesquisar cidade, vila, parque…': 'Search city, village, park…',
  'Línguas Faladas': 'Spoken languages',
  'concluídas': 'completed',
  'Mover para cima': 'Move up',
  'Mover para baixo': 'Move down',
  'Arrastar para ordenar': 'Drag to reorder',
  '+ Adicionar recomendação': '+ Add recommendation',
  'Mover ponto para cima': 'Move point up',
  'Mover ponto para baixo': 'Move point down',
  '+ Adicionar ponto': '+ Add point',
  '+ Adicionar ponto negativo': '+ Add drawback',
  '+ Adicionar ponto positivo': '+ Add highlight',
  'dia está definido': 'day is set',
  'dias estão definidos': 'days are set',
  ' com base nas datas da viagem. Adiciona as atividades diretamente no dia; numa viagem multidestino, escolhe o destino no filtro acima para associar a atividade.': ' based on the trip dates. Add activities directly to the day; on a multi-destination trip, choose the destination in the filter above to link the activity.',
  '(sem nome)': '(unnamed)',
  'Foto de capa': 'Cover photo',
  'Arrasta para reordenar': 'Drag to reorder',
  'Mover para a esquerda': 'Move left',
  'Mover para a direita': 'Move right',
  // Legal sheets: list items and the text fragments that follow emphasis tags
  'Qualquer conteúdo para divulgar negócios próprios, serviços, produtos ou que tenha fins comerciais diretos.': 'Any content promoting your own businesses, services or products, or with direct commercial purposes.',
  'Publicidade, promoções, links de afiliação ou convites para compras.': 'Advertising, promotions, affiliate links or invitations to buy.',
  'Mensagens repetitivas, convites não solicitados ou conteúdo duplicado.': 'Repeated messages, unsolicited invitations or duplicate content.',
  'Material discriminatório, racista, sexista ou que viole direitos humanos.': 'Discriminatory, racist or sexist material, or material that violates human rights.',
  'Conteúdo que promova violência ou comportamentos perigosos.': 'Content promoting violence or dangerous behaviour.',
  'Conteúdo que explore ou abuse de menores ou animais.': 'Content that exploits or abuses children or animals.',
  'Falsificação de identidade, phishing, fraude ou roubo.': 'Identity forgery, phishing, fraud or theft.',
  'Bullying, ameaças, intimidação ou assédio sexual.': 'Bullying, threats, intimidation or sexual harassment.',
  'Conteúdo que viole a privacidade de outras pessoas sem consentimento.': 'Content violating other people’s privacy without consent.',
  'Afirmar ter visitado locais que não visitou ou descrever experiências fictícias.': 'Claiming to have visited places you did not visit or describing fictional experiences.',
  'Nome, apelido, endereço de correio eletrónico, país e cidade durante o registo': 'Name, surname, email address, country and city during registration',
  'Nome de utilizador único para identificar na comunidade': 'Unique username to identify you in the community',
  'Fotografia de perfil e descrição pessoal (opcional)': 'Profile photo and personal description (optional)',
  'Fotografias, vídeos e textos sobre as suas viagens': 'Photos, videos and text about your trips',
  'Comentários, avaliações e interações com outros utilizadores': 'Comments, ratings and interactions with other users',
  'Informações de contacto quando nos contacta': 'Contact information when you contact us',
  'Dados de localização das suas viagens (se fornecidos)': 'Location data for your trips (if provided)',
  'Endereço IP do seu dispositivo': 'Your device IP address',
  'Tipo de navegador, sistema operativo e informações do dispositivo': 'Browser type, operating system and device information',
  'Páginas visitadas e tempo gasto no site': 'Pages visited and time spent on the site',
  'Localização aproximada (baseada em IP)': 'Approximate location (based on IP)',
  'Cookies e tecnologias similares de rastreamento': 'Cookies and similar tracking technologies',
  'Dados de utilização e desempenho do serviço': 'Service usage and performance data',
  'Criar e manter a sua conta, processar o seu conteúdo': 'Create and maintain your account, and process your content',
  'Personalizar o serviço baseado nas suas preferências': 'Personalise the service based on your preferences',
  'Enviar notificações, confirmações de email e atualizações': 'Send notifications, email confirmations and updates',
  'Detetar e prevenir fraudes, abuso e comportamento ilegal': 'Detect and prevent fraud, abuse and illegal behaviour',
  'Processar denúncias e garantir cumprimento dos Termos': 'Process reports and ensure compliance with the Terms',
  'Compreender como os utilizadores usam a plataforma': 'Understand how users use the platform',
  'Cumprir obrigações legais e regulamentares': 'Meet legal and regulatory obligations',
  'Encriptação SSL/TLS para todas as transmissões': 'SSL/TLS encryption for all transmissions',
  'Encriptação de senhas em bases de dados': 'Password encryption in databases',
  'Firewalls e proteção contra intrusions': 'Firewalls and protection against intrusions',
  'Controlo de acesso baseado em privilégios': 'Privilege-based access control',
  'Monitorização contínua de segurança': 'Continuous security monitoring',
  'Aceder às informações pessoais que possuímos sobre si': 'Access the personal information we hold about you',
  'Corrigir informações imprecisas ou incompletas': 'Correct inaccurate or incomplete information',
  'Solicitar a eliminação dos seus dados pessoais': 'Request deletion of your personal data',
  'Restringir o processamento dos seus dados': 'Restrict processing of your data',
  'Receber os seus dados num formato estruturado': 'Receive your data in a structured format',
  'Opor-se ao processamento para fins de marketing': 'Object to processing for marketing purposes',
  'Nunca vender dados de utilizadores a terceiros': 'Never sell user data to third parties',
  'Ser transparentes sobre como recolhemos e usamos dados': 'Be transparent about how we collect and use data',
  'Respeitar as escolhas de privacidade dos utilizadores': 'Respect users’ privacy choices',
  'Cumprir com todas as regulamentações de proteção de dados aplicáveis': 'Comply with all applicable data protection regulations',
  'Manter dados seguros e protegidos': 'Keep data safe and protected',
  'Cookies essenciais:': 'Essential cookies:',
  'necessários para autenticação, segurança, prevenção de abuso e funcionamento básico da Globe Memories.': 'needed for authentication, security, abuse prevention and basic Globe Memories operation.',
  'podem guardar idioma, opções de apresentação e escolhas feitas na plataforma, para não ter de as repetir.': 'may save language, display options and choices made in the platform so you do not have to repeat them.',
  'apenas quando esta funcionalidade estiver ativa, para perceber de forma agregada quais as áreas que precisam de melhorias.': 'only when this feature is enabled, to understand in aggregate which areas need improvement.',
  'Email para assuntos gerais e privacidade:': 'General and privacy email:',
  'Email de Suporte técnico:': 'Technical support email:',
  'Formulário: Através da opção "Fala Connosco" na plataforma': 'Form: through the “Contact us” option in the platform',
  'Através do formulário "Fala Connosco" na plataforma': 'Through the “Contact us” form in the platform',
  'O Globe Memories é uma': 'Globe Memories is a',
  'comunidade digital de viajantes': 'digital traveller community',
  'que permite aos membros documentar, organizar e partilhar as suas autênticas memórias de viagem através de fotografias, vídeos, textos e outras formas de conteúdo multimédia. Esta é uma plataforma': 'that lets members document, organise and share authentic trip stories through photos, videos, text and other multimedia content. This is a platform',
  'exclusivamente para partilha de experiências de viagem pessoais': 'exclusively for sharing personal travel experiences',
  ', não para fins comerciais ou de divulgação de serviços.': ', not for commercial purposes or service promotion.',
  'Não vendemos, não alugamos e não partilhamos as suas informações pessoais com terceiros para fins de marketing comercial.': 'We do not sell, rent or share your personal information with third parties for commercial marketing.',
  'Isto é fundamental para a confiança na comunidade.': 'This is fundamental to community trust.',
  'Nenhum método é 100% seguro. Você é responsável pela sua palavra-passe.': 'No method is 100% secure. You are responsible for your password.',
  'O nosso serviço não se destina a menores de 16 anos.': 'Our service is not intended for children under 16.',
  'Não recolhemos intencionalmente informações pessoais de menores de 16 anos. Utilizadores entre 16 e 18 anos podem usar o serviço apenas com consentimento dos pais ou tutores legais.': 'We do not knowingly collect personal information from children under 16. Users aged 16 to 18 may use the service only with parent or guardian consent.',
  'Privacidade é um valor fundamental de Globe Memories.': 'Privacy is a core value at Globe Memories.',
  'Compromisso Fundamental:': 'Core commitment:',
  'Privacidade é um valor fundamental. Nunca vendemos dados a terceiros. Somos transparentes e respeitamos as suas escolhas de privacidade.': 'Privacy is a core value. We never sell data to third parties. We are transparent and respect your privacy choices.',
  'Preferências:': 'Preferences:',
  'Medição e desempenho:': 'Measurement and performance:',
  'A tua viagem': 'Your trip',
  'A viagem': 'The trip',
  'Descrição curta': 'Short description',
  'Descrição completa': 'Full description',
  'Capa obrigatória: adiciona uma imagem ou vídeo de capa para continuar.': 'Cover required: add a cover image or video to continue.',
  'Preenche o nome da viagem para continuar.': 'Enter the trip name to continue.',
  'Indica um destino ou local para continuar.': 'Enter a destination or place to continue.',
  'Publicar viagem': 'Publish trip',
  'Guardar viagem atualizada': 'Save updated trip',
  'A minha viagem': 'My trip',
  'Viagens públicas': 'Public trips',
  'Viagens privadas': 'Private trips',
  'Apenas seguidores': 'Followers only',
  'Nenhuma viagem encontrada': 'No trips found',
  'Tente ajustar os filtros ou explorar outras categorias.': 'Try adjusting the filters or exploring other categories.',
  'Filtros': 'Filters',
  'Limpar filtros': 'Clear filters',
  'Aplicar filtros': 'Apply filters',
  'Ordenar por': 'Sort by',
  'Mais recentes': 'Most recent',
  'Mais populares': 'Most popular',
  'Viajantes encontrados': 'Travellers found',
  'Mais Viagens': 'More trips',
  'Perfil público': 'Public profile',
  'Perfil privado': 'Private profile',
  'Localização': 'Location',
  'Reputação': 'Reputation',
  'Avalia este viajante': 'Rate this traveller',
  'Viagens publicadas': 'Published trips',
  'Viagens guardadas': 'Saved trips',
  'Nenhuma viagem publicada': 'No published trips',
  'Segue este viajante para veres novas viagens no teu feed.': 'Follow this traveller to see new trips in your feed.',
  'Ainda sem viagens por aqui': 'No trips here yet',
  'Inicia sessão para ver um feed personalizado com viagens da tua rede.': 'Log in to see a personalised feed with trips from your network.',
  'A atualizar viagens': 'Refreshing trips',
  'Mais tarde': 'Later',
  'Hoje': 'Today',
  'hoje': 'today',
  'Ontem': 'Yesterday',
  'ontem': 'yesterday',
  'Agora': 'Now',
  'agora': 'now',
  'por ler': 'unread',
  'lida': 'read',
  'lidas': 'read',
  'Página não encontrada': 'Page not found',
  'Ou explore diretamente': 'Or explore directly',
  'Recarregar feed': 'Refresh feed',
  'Feedback enviado!': 'Feedback sent!',
  'Obrigado por ajudares a melhorar a Globe Memories. A nossa equipa vai analisar o teu feedback.': 'Thank you for helping improve Globe Memories. Our team will review your feedback.',
  'Como podemos ajudar?': 'How can we help?',
  'Escolhe o tipo de feedback e descreve a tua questão com o máximo de detalhe possível.': 'Choose the type of feedback and describe your issue in as much detail as possible.',
  'Tipo de feedback': 'Feedback type',
  'Esta informação ajuda-nos a encaminhar melhor a tua mensagem.': 'This information helps us route your message.',
  'Detalhes': 'Details',
  'Sê o mais específico possível — passos, ecrã, comportamento esperado vs. atual.': 'Be as specific as possible — steps, screen, expected vs. current behaviour.',
  'Ex: Botão de guardar não funciona': 'E.g. Save button does not work',
  'Descreve o problema ou sugestão com o máximo de detalhe possível...': 'Describe the problem or suggestion in as much detail as possible…',
  'Eliminar pergunta': 'Delete question',
  'Remover gosto': 'Remove like',
  'Dar gosto': 'Like',
  'Esconder comentários': 'Hide comments',
  'Escolher fórum': 'Choose forum',
  'Qual é a sua dúvida?': 'What is your question?',
  'Selecione uma categoria': 'Select a category',
  'Selecione um país': 'Select a country',
  'Pesquisar perguntas...': 'Search questions…',
  'Limpar pesquisa': 'Clear search',
  'Ainda não fez nenhuma pergunta': 'No questions yet',
  'Nenhuma pergunta encontrada': 'No questions found',
  'Ainda não há perguntas de seguidores': 'There are no follower questions yet',
  'Insira o seu nome': 'Enter your first name',
  'Insira o seu apelido': 'Enter your last name',
  'A carregar países…': 'Loading countries…',
  'Selecione ou pesquise o país': 'Select or search for a country',
  'Escolha um nome de utilizador': 'Choose a username',
  'Sugestões de nome de utilizador': 'Username suggestions',
  'Insira o seu email': 'Enter your email',
  'Insira a sua palavra-passe': 'Enter your password',
  'Confirme a sua palavra-passe': 'Confirm your password',
  'Insira o seu nome de utilizador ou email': 'Enter your username or email',
  'Nome de utilizador ou email': 'Username or email',
  'Insira a sua nova palavra-passe': 'Enter your new password',
  'Confirme a sua nova palavra-passe': 'Confirm your new password',
  'Ocultar palavra-passe': 'Hide password',
  'Mostrar palavra-passe': 'Show password',
  'Esqueci-me da palavra-passe': 'Forgot password',
  'Lembrar-me': 'Remember me',
  'Continuar com Google': 'Continue with Google',
  'Voltar ao início': 'Back to home',
  'Conhece a Globe Memories': 'Discover Globe Memories',
  'Aprende com quem já foi': 'Learn from people who have been there',
  'Conhece percursos, recomendações e detalhes vindos de quem viveu a viagem.': 'See routes, recommendations and details from people who lived the trip.',
  'Encontra inspiração real': 'Find real inspiration',
  'Ajuda a próxima pessoa': 'Help the next traveller',
  'Partilha o que correu bem, o que aprendeste e o que farias de forma diferente.': 'Share what went well, what you learned and what you would do differently.',
  'Ajuda outros viajantes': 'Help other travellers',
  'Experiências de viagem partilhadas para ajudar outros viajantes.': 'Shared travel experiences to help other travellers.',
  'O que podes fazer na Globe Memories': 'What you can do on Globe Memories',
  'Ir para Início': 'Go to Home',
  'Explorar viagens': 'Explore trips',
  'Viajar': 'Travel',
  'A ferramenta de planeamento de viagens mais completa está a chegar à Globe Memories.': 'The most complete trip planning tool is coming to Globe Memories.',
  'Planeamento Inteligente': 'Smart planning',
  'Viagens em Grupo': 'Group trips',
  'Mapas Interativos Avançados': 'Advanced interactive maps',
  'Pesquise uma cidade para começar': 'Search for a city to get started',
  'Meteorologia temporariamente indisponível': 'Weather temporarily unavailable',
  'A carregar localização': 'Loading location',
  'Usar localização atual': 'Use current location',
  'Painéis de meteorologia': 'Weather panels',
  'Condições atuais': 'Current conditions',
  'Vento e chuva previstos': 'Forecast wind and rain',
  'Previsão horária': 'Hourly forecast',
  'PREVISÃO': 'FORECAST',
  '10 DIAS': '10 DAYS',
  'Detalhes do tempo': 'Weather details',
  'SENSAÇÃO': 'FEELS LIKE',
  'ÍNDICE DE CONFORTO': 'COMFORT INDEX',
  'VENTO': 'WIND',
  'PRECIPITAÇÃO': 'PRECIPITATION',
  'SUGESTÃO PARA HOJE': 'SUGGESTION FOR TODAY',
  'CLIMA NA DATA SELECIONADA': 'WEATHER ON SELECTED DATE',
  'Pesquisar e planear': 'Search and plan',
  'Escolhe uma data ou destino': 'Choose a date or destination',
  'PESQUISAR DATA': 'SEARCH DATE',
  'Selecionar data futura': 'Select a future date',
  'Limpar data': 'Clear date',
  'PESQUISAS RECENTES': 'RECENT SEARCHES',
  'Limpar': 'Clear',
  'DESTINOS': 'DESTINATIONS',
  'Fechar pesquisa': 'Close search',
  'Temperatura': 'Temperature',
  'Probabilidade de chuva': 'Rain probability',
  'Velocidade do vento': 'Wind speed',
  'Clima na data selecionada': 'Weather on selected date',
  'Pesquisar data específica': 'Search a specific date',
  'Histórico recente': 'Recent history',
  'Limpar histórico': 'Clear history',
  'Suas viagens': 'Your trips',
  'Viagens passadas': 'Past trips',
  'Vento máx.': 'Max wind',
  'Chuva': 'Rain',
  'Estás em dia': 'You are all caught up',
  'Ainda sem notificações': 'No notifications yet',
  'Marca todas como lidas': 'Mark all as read',
  'Todas': 'All',
  'Não lidas': 'Unread',
  'Marcar como lida': 'Mark as read',
  'Mais opções': 'More options',
  'Aceitar identificação': 'Accept identification',
  'Rejeitar identificação': 'Reject identification',
  'Aceitar pedido': 'Accept request',
  'Rejeitar pedido': 'Reject request',
  'Globe Memories | Descobre o mundo e partilha as tuas viagens': 'Globe Memories | Discover the world and share your trips',
  'Início | Globe Memories': 'Home | Globe Memories',
  'Descobrir viagens | Globe Memories': 'Discover trips | Globe Memories',
  'Mapa de viagens | Globe Memories': 'Trip map | Globe Memories',
  'Meteorologia | Globe Memories': 'Weather | Globe Memories',
  'Fórum de viajantes | Globe Memories': 'Traveller forum | Globe Memories',
  'Notificações | Globe Memories': 'Notifications | Globe Memories',
  'Definições e privacidade | Globe Memories': 'Settings and privacy | Globe Memories',
  'Enviar feedback | Globe Memories': 'Send feedback | Globe Memories',
  'Ajuda e suporte | Globe Memories': 'Help & support | Globe Memories',
  'Conquistas | Globe Memories': 'Achievements | Globe Memories',
  'Planear viagem | Globe Memories': 'Plan a trip | Globe Memories',
  'Entrar | Globe Memories': 'Log in | Globe Memories',
  'Criar conta | Globe Memories': 'Create account | Globe Memories',
  'Criar viagem | Globe Memories': 'Create trip | Globe Memories',
  'Editar viagem | Globe Memories': 'Edit trip | Globe Memories',
  'Editar perfil | Globe Memories': 'Edit profile | Globe Memories',
  'Página não encontrada | Globe Memories': 'Page not found | Globe Memories',
  'Administração | Globe Memories': 'Administration | Globe Memories',
  'Acesso administrativo | Globe Memories': 'Admin access | Globe Memories',

  // Remaining public-facing pages and dialogs.  These strings are kept in
  // the same source dictionary so the DOM observer also translates lazy
  // routes that do not use the hook directly.
  'A Sua Localização': 'Your location',
  'A app é uma PWA. Para desinstalar, remove o atalho da home screen (long-press → remover) ou apaga pelos ajustes do sistema.': 'This app is a PWA. To uninstall it, remove the home-screen shortcut or delete it in your device settings.',
  'A localização devolvida pelo dispositivo é inválida. Pesquisa uma cidade manualmente.': 'The location returned by your device is invalid. Search for a city manually.',
  'A nossa comunidade está a crescer — volte em breve!': 'Our community is growing — come back soon!',
  'Abrir vídeo': 'Open video',
  'Acomodações': 'Accommodation',
  'Adiciona hotéis, casas ou outros alojamentos e, se fizer sentido, o regime escolhido.': 'Add hotels, homes or other accommodation and, if relevant, the selected board basis.',
  'Adiciona os países e cidades, vilas ou locais que fizeram parte da viagem.': 'Add the countries and cities, villages or places that were part of the trip.',
  'Adiciona uma fotografia de capa, título e resumo.': 'Add a cover photo, title and summary.',
  'Adicionar imagens à viagem': 'Add images to the trip',
  'Adicionar publicação': 'Add post',
  'Agora não': 'Not now',
  'Alertas push no telemóvel e computador': 'Push alerts on mobile and desktop',
  'Algo não está a funcionar corretamente': 'Something is not working correctly',
  'Alguém': 'Someone',
  'Ameaças': 'Threats',
  'Apagar todas as notificações? Esta ação não pode ser revertida.': 'Delete all notifications? This action cannot be undone.',
  'Apelido demasiado longo (máx. 50).': 'Last name is too long (max. 50).',
  'Apelido é obrigatório.': 'Last name is required.',
  'Aqui defines o nome, a capa e o texto que vai ajudar outros viajantes a perceber a experiência.': 'Set the name, cover and text that will help other travellers understand the experience.',
  'Arraste para ajustar a posição': 'Drag to adjust the position',
  'As datas organizam o calendário da viagem e os custos ajudam a criar uma memória mais útil.': 'Dates organise the trip calendar and costs help create a more useful record.',
  'As definições de privacidade estão atualizadas.': 'Privacy settings are up to date.',
  'As notificações push serão disponibilizadas após a configuração do serviço.': 'Push notifications will be available after the service is configured.',
  'As palavras-passe não coincidem.': 'Passwords do not match.',
  'As regras da nossa comunidade. Leia com atenção.': 'Our community rules. Please read them carefully.',
  'Associar fotografia a vários elementos da viagem': 'Associate a photo with multiple trip elements',
  'Associar vídeo a vários elementos da viagem': 'Associate a video with multiple trip elements',
  'Assédio / abuso': 'Harassment / abuse',
  'Assédio ou comportamento inadequado': 'Harassment or inappropriate behaviour',
  'Ativa a permissão nas definições do navegador para voltar a receber alertas.': 'Enable permission in your browser settings to receive alerts again.',
  'Ativar som do vídeo': 'Turn video sound on',
  'Avaliação da viagem': 'Trip rating',
  'Ações da viagem': 'Trip actions',
  'Boas práticas para a tua conta': 'Account best practices',
  'Citação': 'Quote',
  'Comentário': 'Comment',
  'Comentário adicionado.': 'Comment added.',
  'Comentário eliminado.': 'Comment deleted.',
  'Comentário publicado.': 'Comment posted.',
  'Comentário do sistema': 'System comment',
  'Começa por indicar para onde viajaste': 'Start by telling us where you travelled',
  'Começar a preencher': 'Start filling it in',
  'Como acedo à minha conta?': 'How do I access my account?',
  'Como adicionar memórias e fotografias às minhas viagens?': 'How do I add memories and photos to my trips?',
  'Como ajustar as definições de privacidade?': 'How do I adjust privacy settings?',
  'Como contacto o suporte técnico?': 'How do I contact technical support?',
  'Como desinstalar a aplicação no meu dispositivo móvel?': 'How do I uninstall the app from my mobile device?',
  'Como funciona o sistema de comentários e avaliações?': 'How do comments and ratings work?',
  'Como tratamos os seus dados pessoais. Transparência total.': 'How we handle your personal data. Full transparency.',
  'Condições estáveis': 'Stable conditions',
  'Confirma a pré-visualização, escolhe a privacidade e publica quando estiveres satisfeito.': 'Check the preview, choose privacy and publish when you are happy.',
  'Conquistar um troféu': 'Earn a trophy',
  'Constrói o itinerário dia a dia': 'Build the itinerary day by day',
  'Conta suspensa. Contacta o suporte para mais informações.': 'Account suspended. Contact support for more information.',
  'Contador de Histórias': 'Storyteller',
  'Conte a história da viagem. Quanto mais detalhe, mais útil para a comunidade.': 'Tell the story of the trip. The more detail, the more useful it is for the community.',
  'Conteúdo inapropriado': 'Inappropriate content',
  'Controlo separado para estatísticas gerais e monetárias': 'Separate controls for general and monetary statistics',
  'Controlos do vídeo': 'Video controls',
  'Custo de alimentação': 'Food cost',
  'Custo total da viagem (obrigatório)': 'Total trip cost (required)',
  'Define a data de início e fim no passo "Datas e custos" para gerar os dias do itinerário.': 'Set the start and end dates in the “Dates & costs” step to generate itinerary days.',
  'Define a viagem como pública ou privada e usa “Publicar viagem” para terminar.': 'Set the trip as public or private and use “Publish trip” to finish.',
  'Denunciar publicação': 'Report post',
  'Descobre viajantes para começares a acompanhar novas memórias.': 'Discover travellers to follow and see new trip stories.',
  'Descreva detalhadamente a sua questão...': 'Describe your question in detail…',
  'Descreva o motivo da denúncia...': 'Describe the reason for the report…',
  'Descrição válida': 'Valid description',
  'Descubra locais únicos e experiências imperdíveis.': 'Discover unique places and unforgettable experiences.',
  'Destino Único': 'Single destination',
  'Detalhes do preço': 'Price details',
  'Dicas de Segurança': 'Safety tips',
  'Documentação legal': 'Legal documentation',
  'Download ou eliminação permanente': 'Download or permanent deletion',
  'Dá identidade à tua viagem': 'Give your trip its identity',
  'Dá vida à memória com fotos e vídeos': 'Bring the trip to life with photos and videos',
  'Eliminar comentário': 'Delete comment',
  'Eliminar este comentário?': 'Delete this comment?',
  'Email é obrigatório': 'Email is required',
  'Encontre qualquer país, cidade ou ponto de interesse.': 'Find any country, city or point of interest.',
  'Erro ao adicionar comentário.': 'Error adding comment.',
  'Erro ao eliminar comentário.': 'Error deleting comment.',
  'Erro ao terminar sessão.': 'Error ending session.',
  'Erro ao terminar sessões.': 'Error ending sessions.',
  'Escolhe a capa, adiciona fotografias e vídeos e define a ordem.': 'Choose the cover, add photos and videos, and set the order.',
  'Escolhe destino único ou vários destinos.': 'Choose one destination or several destinations.',
  'Escolhe hoje ou uma data futura para consultar a previsão.': 'Choose today or a future date to view the forecast.',
  'Escreva um comentário...': 'Write a comment…',
  'Escreve a descrição da viagem...': 'Write the trip description…',
  'Esta é a foto de capa / destaque da viagem': 'This is the trip cover / featured photo',
  'Estas notas tornam a viagem mais honesta e útil para a comunidade.': 'These notes make the trip more honest and useful to the community.',
  'Estatísticas do Perfil': 'Profile statistics',
  'Este mês': 'This month',
  'Este navegador não suporta geolocalização. Pesquisa uma cidade manualmente.': 'This browser does not support geolocation. Search for a city manually.',
  'Este navegador não suporta notificações push.': 'This browser does not support push notifications.',
  'Este pedido já não está pendente.': 'This request is no longer pending.',
  'Ex.: Bacalhau à Brás': 'E.g. cod with potatoes',
  'Experiência · Alimentação': 'Experience · Food',
  'Experiência · Alojamento': 'Experience · Accommodation',
  'Experiência · Extras': 'Experience · Extras',
  'Experiência · Informações': 'Experience · Information',
  'Experiência · Itinerário': 'Experience · Itinerary',
  'Experiência · Locais': 'Experience · Places',
  'Experiência · Transportes': 'Experience · Transport',
  'Explore o mundo com países visitados destacados em turquesa.': 'Explore the world with visited countries highlighted in turquoise.',
  'Filtrar itinerário por destino': 'Filter itinerary by destination',
  'Formatação da descrição': 'Description formatting',
  'Formato de email inválido': 'Invalid email format',
  'Foto ou vídeo de capa (obrigatório)': 'Cover photo or video (required)',
  'Fotógrafo de Viagens': 'Travel Photographer',
  'Google Login ainda não está configurado no servidor.': 'Google login is not configured on the server yet.',
  'Google Login indisponível.': 'Google login unavailable.',
  'Google login só está disponível no browser.': 'Google login is only available in the browser.',
  'Guarda os pontos de referência': 'Save points of interest',
  'Guarda restaurantes, pratos ou experiências gastronómicas que valeram a pena.': 'Save restaurants, dishes or food experiences worth recommending.',
  'Guardar definições de privacidade': 'Save privacy settings',
  'Histórico limpo.': 'History cleared.',
  'Imagens, descrições ou publicações ofensivas, nudez, etc.': 'Offensive images, descriptions or posts, nudity, etc.',
  'Indica o custo total, moeda, notas e a divisão por voo, alojamento, alimentação e extras.': 'Enter the total cost, currency, notes and the breakdown for flights, accommodation, food and extras.',
  'Indica o tipo de refeição ou recomendação.': 'Enter the type of meal or recommendation.',
  'Indica os idiomas usados ou ouvidos durante a experiência.': 'Enter the languages used or heard during the experience.',
  'Informação da viagem': 'Trip information',
  'Informação falsa': 'False information',
  'Informação falsa ou enganosa': 'False or misleading information',
  'Informação legal': 'Legal information',
  'Inicie sessão para bloquear viajantes.': 'Log in to block travellers.',
  'Inicie sessão para comentar!': 'Log in to comment!',
  'Inicie sessão para denunciar.': 'Log in to report.',
  'Inicie sessão para gostar!': 'Log in to like this!',
  'Inicie sessão para guardar viagens.': 'Log in to save trips.',
  'Ir para a secção': 'Go to section',
  'Ir para os comentários': 'Go to comments',
  'Itálico': 'Italic',
  'Linguagem agressiva, insultos, bullying, provocações.': 'Aggressive language, insults, bullying or provocation.',
  'Localização atual obtida com sucesso!': 'Current location found successfully!',
  'Localização da viagem': 'Trip location',
  'Localização obtida, mas não foi possível identificar a cidade. A meteorologia continua disponível; pesquisa uma cidade manualmente se necessário.': 'Location found, but the city could not be identified. Weather remains available; search for a city manually if needed.',
  'Mapa indisponível': 'Map unavailable',
  'Mensagem deve ter no máximo 1000 caracteres': 'Message must be at most 1,000 characters',
  'Mensagem é obrigatória': 'Message is required',
  'Mensagens, comentários ou perseguição indesejada.': 'Unwanted messages, comments or stalking.',
  'Mover recomendação para baixo': 'Move recommendation down',
  'Mover recomendação para cima': 'Move recommendation up',
  'Mostra informações meteorológicas em tempo real dos teus destinos — temperatura, humidade, vento, etc. Ajuda-te a planear a roupa e o que levar para cada viagem.': 'See real-time weather for your destinations — temperature, humidity, wind and more. Plan what to wear and pack for every trip.',
  'Mostrar confirmação': 'Show confirmation',
  'Na secção "Viagens Futuras", podes criar e organizar viagens que tens em mente — destinos, datas, lista de itens, informações do local, clima previsto. Quando completares, moves para "As Minhas Viagens".': 'In “Future trips”, you can create and organise trips you have in mind — destinations, dates, packing lists, place information and forecast weather. When complete, move them to “My trips”.',
  'Nacionalidade demasiado longa (máx. 50).': 'Nationality is too long (max. 50).',
  'Nenhum país encontrado': 'No country found',
  'Nome da cidade não pode exceder 50 caracteres!': 'City name cannot exceed 50 characters!',
  'Nome de utilizador é obrigatório.': 'Username is required.',
  'Nome demasiado longo (máx. 50).': 'Name is too long (max. 50).',
  'Nome é obrigatório': 'Name is required',
  'Nome é obrigatório.': 'Name is required.',
  'Notificações apagadas.': 'Notifications deleted.',
  'Notificações push ativadas.': 'Push notifications enabled.',
  'Notificações push desativadas.': 'Push notifications disabled.',
  'Não foi possível aceitar o pedido de identificação.': 'Could not accept the trip identification request.',
  'Não foi possível aceitar o pedido.': 'Could not accept the request.',
  'Não foi possível apagar a notificação.': 'Could not delete the notification.',
  'Não foi possível apagar as notificações.': 'Could not delete the notifications.',
  'Não foi possível atualizar a relação.': 'Could not update the relationship.',
  'Não foi possível atualizar a relação. Tenta novamente.': 'Could not update the relationship. Try again.',
  'Não foi possível atualizar a tua lista de guardados.': 'Could not update your saved list.',
  'Não foi possível atualizar o gosto.': 'Could not update the like.',
  'Não foi possível bloquear este viajante.': 'Could not block this traveller.',
  'Não foi possível carregar as notificações.': 'Could not load notifications.',
  'Não foi possível carregar o Google Login.': 'Could not load Google login.',
  'Não foi possível carregar o catálogo.': 'Could not load the catalogue.',
  'Não foi possível concluir a pesquisa. Tenta novamente.': 'Could not complete the search. Try again.',
  'Não foi possível confirmar o destino. Volta a selecionar o local da viagem.': 'Could not confirm the destination. Select the trip location again.',
  'Não foi possível denunciar esta viagem.': 'Could not report this trip.',
  'Não foi possível denunciar este utilizador.': 'Could not report this user.',
  'Não foi possível desbloquear este viajante.': 'Could not unblock this traveller.',
  'Não foi possível eliminar a conta.': 'Could not delete the account.',
  'Não foi possível eliminar o comentário.': 'Could not delete the comment.',
  'Não foi possível identificar a viagem.': 'Could not identify the trip.',
  'Não foi possível identificar o utilizador.': 'Could not identify the user.',
  'Não foi possível marcar tudo como lido.': 'Could not mark everything as read.',
  'Não foi possível obter a previsão exata desta data. A mostrar uma estimativa climática.': 'Could not get the exact forecast for this date. Showing a weather estimate.',
  'Não foi possível preparar a imagem.': 'Could not prepare the image.',
  'Não foi possível procurar viajantes.': 'Could not search for travellers.',
  'Não foi possível publicar o comentário.': 'Could not post the comment.',
  'Não foi possível rejeitar o pedido de identificação.': 'Could not reject the trip identification request.',
  'Não foi possível rejeitar o pedido.': 'Could not reject the request.',
  'Não foi possível remover dos guardados.': 'Could not remove from saved.',
  'Não tens notificações por ler. Volta mais tarde para ver as últimas.': 'You have no unread notifications. Come back later to see the latest ones.',
  'Número de pessoas incluídas no custo': 'Number of people included in the cost',
  'O Mapa Interativo mostra todas as tuas viagens e memórias num mapa-múndi. Vês cada localização, rotas e atividades. Clicando nas marcações, acedes às fotos e descrições.': 'The Interactive Map shows all your trips and stories on a world map. See every location, route and activity, then open photos and descriptions from the markers.',
  'O email é demasiado longo': 'Email is too long',
  'O email é obrigatório': 'Email is required',
  'O formato do email é inválido': 'Email format is invalid',
  'O nome de utilizador deve conter apenas letras, números, pontos ou underscore': 'Username may only contain letters, numbers, dots or underscores',
  'O nome de utilizador deve ter no máximo 20 caracteres': 'Username must be at most 20 characters',
  'O nome de utilizador não pode conter espaços': 'Username cannot contain spaces',
  'O nome de utilizador é obrigatório': 'Username is required',
  'O primeiro nome é demasiado longo': 'First name is too long',
  'O primeiro nome é obrigatório': 'First name is required',
  'O que são Conquistas e como as obtenho?': 'What are Achievements and how do I earn them?',
  'O que é a funcionalidade de Clima (Weather)?': 'What is the Weather feature?',
  'O que é o Mapa Interativo e como funciona?': 'What is the Interactive Map and how does it work?',
  'Os dados meteorológicos estão temporariamente indisponíveis. Tenta novamente dentro de instantes.': 'Weather data is temporarily unavailable. Try again in a moment.',
  'Os dias mantêm sempre a ordem cronológica: Dia 1, Dia 2, Dia 3…': 'Days always remain in chronological order: Day 1, Day 2, Day 3…',
  'Os meus dados estão seguros?': 'Is my data safe?',
  'Outro motivo que não se enquadra nas opções acima.': 'Another reason not covered above.',
  'Podes acrescentar noites, localização, avaliação e observações úteis.': 'You can add nights, location, a rating and useful notes.',
  'Podes adicionar mais locais e remover os que não pertencem à viagem.': 'You can add more places and remove those that do not belong to the trip.',
  'Podes deixar comentários e avaliações nas viagens e memórias de outros utilizadores (se permitido). Cria-se uma comunidade onde trocam-se dicas e recomendações.': 'You can comment on and rate other users’ trips and stories (when allowed). Build a community that exchanges tips and recommendations.',
  'Podes ordenar os locais e acrescentar uma descrição ou recomendação.': 'You can order places and add a description or recommendation.',
  'Podes voltar a qualquer tab para corrigir ou completar informação.': 'You can return to any tab to correct or complete information.',
  'Ponto Turístico': 'Tourist attraction',
  'Por favor, corrija os erros no formulário.': 'Please correct the errors in the form.',
  'Por favor, insira uma cidade válida (mínimo 2 caracteres).': 'Please enter a valid city (at least 2 characters).',
  'Preenche as datas de início e fim e, se quiseres, a data da reserva.': 'Enter the start and end dates and, if you want, the booking date.',
  'Preenche todos os campos obrigatórios.': 'Complete all required fields.',
  'Progresso da publicação': 'Publishing progress',
  'Progresso do carregamento de fotos e vídeos': 'Photo and video upload progress',
  'Progresso do vídeo': 'Video progress',
  'Pré-visualização da capa': 'Cover preview',
  'Queres partilhar a tua opinião geral': 'Would you like to share your overall opinion',
  'Recebe alertas de comentários, gostos, seguidores e avisos importantes.': 'Receive alerts about comments, likes, followers and important notices.',
  'Recebes alertas mesmo quando o Globe Memories está fechado.': 'Receive alerts even when Globe Memories is closed.',
  'Regista pontos positivos, aspetos menos bons e qualquer nota que não caiba nas outras secções.': 'Record positives, drawbacks and any note that does not fit elsewhere.',
  'Região': 'Region',
  'Região não especificada': 'Region not specified',
  'Remover formatação': 'Remove formatting',
  'Remover imagem ou vídeo de capa': 'Remove cover image or video',
  'Remover vídeo de capa': 'Remove cover video',
  'Reproduzir vídeo': 'Play video',
  'Reproduzir vídeo com som': 'Play video with sound',
  'Responder ao comentário': 'Reply to comment',
  'Revê tudo antes de publicar': 'Review everything before publishing',
  'Se o teu perfil for público, outros utilizadores visitam-no através de "Explorar Viajantes". No perfil público, veem as tuas viagens, descrição e memórias destacadas.': 'If your profile is public, other users can visit it through “Explore travellers”. On your public profile they can see your trips, description and featured stories.',
  'Segue viajantes para veres e participares nas perguntas do fórum de Seguidores.': 'Follow travellers to see and take part in Followers forum questions.',
  'Segura e arrasta para ordenar no telemóvel': 'Press and drag to reorder on mobile',
  'Seleciona primeiro o país': 'Select the country first',
  'Selecione primeiro o país': 'Select the country first',
  'Sem título': 'Untitled',
  'Sessão terminada em todos os dispositivos.': 'Session ended on all devices.',
  'Sim. Todas as informações são encriptadas, guardadas em servidores seguros, e a tua palavra-passe nunca é partilhada. Podes eliminar a tua conta a qualquer momento nas definições.': 'Yes. All information is encrypted and stored on secure servers, and your password is never shared. You can delete your account at any time in Settings.',
  'Sugestão': 'Suggestion',
  'Sugestões inteligentes baseadas no seu perfil e preferências.': 'Smart suggestions based on your profile and preferences.',
  'Sumário válido': 'Valid summary',
  'Só tu': 'Only you',
  'Só tu e os teus seguidores poderão ver, responder e gostar.': 'Only you and your followers can view, reply and like.',
  'Só tu podes ver esta viagem': 'Only you can see this trip',
  'Tem a certeza que quer eliminar este comentário?': 'Are you sure you want to delete this comment?',
  'Tens alterações de privacidade por guardar.': 'You have privacy changes to save.',
  'Usa a descrição para contar o que tornou esta viagem especial e atribui uma avaliação.': 'Use the description to tell what made this trip special and add a rating.',
  'Usa as notas para partilhar custos, duração ou dicas práticas.': 'Use notes to share costs, duration or practical tips.',
  'Use apenas letras, números, pontos ou underscore.': 'Use only letters, numbers, dots or underscores.',
  'Utilizador não autenticado.': 'User is not authenticated.',
  'Valor entre 0 e 1.000.000, com até 2 casas decimais': 'Value between 0 and 1,000,000, with up to 2 decimal places',
  'Ver preços': 'View prices',
  'Viagem com um único destino': 'Trip with one destination',
  'Viagem com vários destinos': 'Trip with multiple destinations',
  'Viagem sem título': 'Untitled trip',
  'Voltar à tab anterior': 'Back to previous tab',
  'Voltar à viagem': 'Back to trip',
  'Vídeo de capa / destaque da viagem': 'Trip cover / featured video',
  // Country names used by the profile and destination selectors.
  'Afeganistão': 'Afghanistan',
  'Albânia': 'Albania',
  'Argélia': 'Algeria',
  'Antígua e Barbuda': 'Antigua and Barbuda',
  'Armênia': 'Armenia',
  'Arábia Saudita': 'Saudi Arabia',
  'Austrália': 'Australia',
  'Azerbaijão': 'Azerbaijan',
  'Bielorrússia': 'Belarus',
  'Bolívia': 'Bolivia',
  'Bulgária': 'Bulgaria',
  'Butão': 'Bhutan',
  'Bélgica': 'Belgium',
  'Bósnia e Herzegovina': 'Bosnia and Herzegovina',
  'Camarões': 'Cameroon',
  'Canadá': 'Canada',
  'Cazaquistão': 'Kazakhstan',
  'Colômbia': 'Colombia',
  'Croácia': 'Croatia',
  'Emirados Árabes Unidos': 'United Arab Emirates',
  'Eslováquia': 'Slovakia',
  'Eslovênia': 'Slovenia',
  'Estônia': 'Estonia',
  'Etiópia': 'Ethiopia',
  'Finlândia': 'Finland',
  'França': 'France',
  'Gabão': 'Gabon',
  'Geórgia': 'Georgia',
  'Grécia': 'Greece',
  'Guiné': 'Guinea',
  'Guiné Equatorial': 'Equatorial Guinea',
  'Guiné-Bissau': 'Guinea-Bissau',
  'Gâmbia': 'The Gambia',
  'Ilhas Maurício': 'Mauritius',
  'Ilhas Salomão': 'Solomon Islands',
  'Índia': 'India',
  'Indonésia': 'Indonesia',
  'Irã': 'Iran',
  'Islândia': 'Iceland',
  'Itália': 'Italy',
  'Iémen': 'Yemen',
  'Japão': 'Japan',
  'Jordânia': 'Jordan',
  'Letônia': 'Latvia',
  'Líbano': 'Lebanon',
  'Líbia': 'Libya',
  'Lituânia': 'Lithuania',
  'Macedônia do Norte': 'North Macedonia',
  'Madagáscar': 'Madagascar',
  'Malásia': 'Malaysia',
  'Mauritânia': 'Mauritania',
  'México': 'Mexico',
  'Moldávia': 'Moldova',
  'Mongólia': 'Mongolia',
  'Mônaco': 'Monaco',
  'Moçambique': 'Mozambique',
  'Namíbia': 'Namibia',
  'Nicarágua': 'Nicaragua',
  'Nigéria': 'Nigeria',
  'Níger': 'Niger',
  'Nova Zelândia': 'New Zealand',
  'Omã': 'Oman',
  'Paquistão': 'Pakistan',
  'Papua-Nova Guiné': 'Papua New Guinea',
  'Países Baixos': 'Netherlands',
  'Panamá': 'Panama',
  'Polônia': 'Poland',
  'Quênia': 'Kenya',
  'Quirguistão': 'Kyrgyzstan',
  'República Centro-Africana': 'Central African Republic',
  'República Checa': 'Czechia',
  'República Democrática do Congo': 'Democratic Republic of the Congo',
  'República Dominicana': 'Dominican Republic',
  'Romênia': 'Romania',
  'Rússia': 'Russia',
  'Santa Lúcia': 'Saint Lucia',
  'São Tomé e Príncipe': 'São Tomé and Príncipe',
  'Sérvia': 'Serbia',
  'Síria': 'Syria',
  'Somália': 'Somalia',
  'Suazilândia': 'Eswatini',
  'Sudão': 'Sudan',
  'Sudão do Sul': 'South Sudan',
  'Suécia': 'Sweden',
  'Suíça': 'Switzerland',
  'Tailândia': 'Thailand',
  'Tajiquistão': 'Tajikistan',
  'Tanzânia': 'Tanzania',
  'Tunísia': 'Tunisia',
  'Turcomenistão': 'Turkmenistan',
  'Ucrânia': 'Ukraine',
  'Uzbequistão': 'Uzbekistan',
  'Vietnã': 'Vietnam',
  'Zâmbia': 'Zambia',
  'Zimbábue': 'Zimbabwe',
  'África do Sul': 'South Africa',
  'Áustria': 'Austria',
  'Termos e Condições': 'Terms and Conditions',
  'Política de Cookies': 'Cookie Policy',
  'Compreendi': 'I understand',
  'Como utilizamos cookies e armazenamento no seu dispositivo.': 'How we use cookies and storage on your device.',
  '1. Aceitação dos Termos': '1. Acceptance of the Terms',
  '2. Descrição do Serviço': '2. Service Description',
  '3. Conta do Viajante': '3. Traveller Account',
  '3.1 Registo e Dados Precisos': '3.1 Registration and Accurate Data',
  '3.2 Segurança da Conta': '3.2 Account Security',
  '3.3 Idade Mínima': '3.3 Minimum Age',
  '4. Filosofia da Comunidade de Viajantes': '4. Traveller Community Philosophy',
  '5. Conteúdo do Viajante': '5. Traveller Content',
  '5.1 Propriedade': '5.1 Ownership',
  '5.2 Tipos de Conteúdo Permitido': '5.2 Permitted Content Types',
  '5.3 Conteúdo Proibido': '5.3 Prohibited Content',
  '6. Denúncias e Moderação': '6. Reports and Moderation',
  '6.1 Sistema de Denúncia de Conteúdo': '6.1 Content Reporting System',
  '6.2 Denúncia de Utilizador': '6.2 User Reports',
  '6.3 Processo de Moderação': '6.3 Moderation Process',
  '7. Uso Aceitável': '7. Acceptable Use',
  '8. Propriedade Intelectual': '8. Intellectual Property',
  '9. Modificações do Serviço': '9. Service Changes',
  '10. Limitação de Responsabilidade': '10. Limitation of Liability',
  '11. Rescisão': '11. Termination',
  '12. Alterações aos Termos': '12. Changes to the Terms',
  '13. Lei Aplicável e Jurisdição': '13. Governing Law and Jurisdiction',
  '14. Contacto': '14. Contact',
  '1. Informações que Recolhemos': '1. Information We Collect',
  '1.1 Informações Fornecidas por Si': '1.1 Information You Provide',
  '1.2 Informações Recolhidas Automaticamente': '1.2 Information Collected Automatically',
  '2. Como Utilizamos as suas Informações': '2. How We Use Your Information',
  '3. Partilha de Informações': '3. Information Sharing',
  '3.1 Partilha Pública': '3.1 Public Sharing',
  '3.2 Não Partilha com Terceiros para Fins Comerciais': '3.2 No Sharing with Third Parties for Commercial Purposes',
  '3.3 Prestadores de Serviços Técnicos': '3.3 Technical Service Providers',
  '3.4 Requisitos Legais': '3.4 Legal Requirements',
  '4. Segurança dos Dados': '4. Data Security',
  '5. Cookies e Tecnologias de Rastreamento': '5. Cookies and Tracking Technologies',
  '6. Os Seus Direitos de Dados (RGPD)': '6. Your Data Rights (GDPR)',
  '7. Retenção de Dados': '7. Data Retention',
  '8. Transferências Internacionais': '8. International Transfers',
  '9. Protecção de Menores': '9. Protection of Minors',
  '10. Alterações a esta Política': '10. Changes to this Policy',
  '11. Contacto e Direitos': '11. Contact and Rights',
  '12. Compromisso com Privacidade': '12. Privacy Commitment',
  'Autenticidade:': 'Authenticity:',
  'Comunidade:': 'Community:',
  'Inspiração:': 'Inspiration:',
  'Integridade:': 'Integrity:',
  'Histórias reais e experiências genuínas de viagem': 'Real stories and genuine travel experiences',
  'Troca mútua de experiências entre viajantes': 'Mutual exchange of experiences between travellers',
  'Inspirar outros a viajar e explorar o mundo': 'Inspire others to travel and explore the world',
  'Relações honestas entre membros da comunidade': 'Honest relationships between community members',
  'Fotografias e vídeos de viagens pessoais': 'Photos and videos of personal trips',
  'Descrições de experiências e memórias de viagem': 'Descriptions of travel experiences and stories',
  'Dicas e recomendações baseadas em experiências reais': 'Tips and recommendations based on real experiences',
  'Avaliações honestas de locais visitados': 'Honest ratings of places visited',
  'Roteiros e itinerários pessoais de viagem': 'Personal travel routes and itineraries',
  'Comentários e feedback em viagens de outros utilizadores': 'Comments and feedback on other users’ trips',
  'Strictamente proibido:': 'Strictly prohibited:',
  'Autopromoção:': 'Self-promotion:',
  'Marketing comercial:': 'Commercial marketing:',
  'Spam:': 'Spam:',
  'Conteúdo ofensivo:': 'Offensive content:',
  'Violência ou perigo:': 'Violence or danger:',
  'Exploração:': 'Exploitation:',
  'Fraude:': 'Fraud:',
  'Assédio:': 'Harassment:',
  'Privacidade de terceiros:': 'Third-party privacy:',
  'Falsificação:': 'Fabrication:',
  'Múltiplas publicações de conteúdo comercial': 'Multiple commercial content posts',
  'Assédio persistente a outros utilizadores': 'Persistent harassment of other users',
  'Spamming contínuo': 'Ongoing spamming',
  'Falsificação de identidade comprovada': 'Proven identity fraud',
  'Conteúdo que viole leis aplicáveis': 'Content that violates applicable laws',
  'Violação repetida das regras da comunidade': 'Repeated violation of community rules',
  'Fornecedores de alojamento na nuvem': 'Cloud hosting providers',
  'Serviços de análise web': 'Web analytics services',
  'Fornecedores de email': 'Email providers',
  'Fornecedores de segurança cibernética': 'Cybersecurity providers',
  'Funcionalidade: Manter a sua sessão ativa e preferências': 'Functionality: keep your session active and save preferences',
  'Segurança: Detectar e prevenir fraudes': 'Security: detect and prevent fraud',
  'Análise: Compreender como utiliza o serviço': 'Analytics: understand how you use the service',
  'Melhorias: Otimizar a experiência de utilizador': 'Improvements: optimise the user experience',
  'Direito de acesso:': 'Right of access:',
  'Direito de retificação:': 'Right to rectification:',
  'Direito ao esquecimento:': 'Right to erasure:',
  'Direito de restrição:': 'Right to restriction:',
  'Direito de portabilidade:': 'Right to data portability:',
  'Direito de oposição:': 'Right to object:',
  'Ao aceder e utilizar o Globe Memories, concorda em cumprir e estar vinculado a estes Termos e Condições de Uso. Se não concordar com qualquer parte destes termos, não deve utilizar o nosso serviço. Estes termos aplicam-se a todos os utilizadores, independentemente da sua localização geográfica.': 'By accessing and using Globe Memories, you agree to comply with and be bound by these Terms and Conditions of Use. If you do not agree with any part of these terms, you must not use our service. These terms apply to all users regardless of their geographical location.',
  'O Globe Memories é uma comunidade digital de viajantes que permite aos membros documentar, organizar e partilhar as suas autênticas memórias de viagem através de fotografias, vídeos, textos e outras formas de conteúdo multimédia. Esta é uma plataforma': 'Globe Memories is a digital traveller community that lets members document, organise and share authentic trip stories through photos, videos, text and other multimedia content. This is a platform',
  'exclusivamente para partilha de experiências de viagem pessoais, não para fins comerciais ou de divulgação de serviços.': 'exclusively for sharing personal travel experiences, not for commercial purposes or service promotion.',
  'Para utilizar o serviço, deve criar uma conta fornecendo informações precisas, atualizadas e completas durante o processo de registo. Deve usar o seu nome verdadeiro ou um pseudónimo consistente, e não pode criar contas falsas, múltiplas ou em nome de terceiros. Assume total responsabilidade pela precisão das informações fornecidas.': 'To use the service, you must create an account with accurate, current and complete information during registration. Use your real name or a consistent pseudonym; do not create fake, multiple or third-party accounts. You are fully responsible for the accuracy of the information you provide.',
  'É responsável por manter a confidencialidade da sua palavra-passe e por todas as atividades que ocorram na sua conta. Deve notificar-nos imediatamente sobre qualquer uso não autorizado, comportamento suspeito ou violação de segurança da sua conta.': 'You are responsible for keeping your password confidential and for all activity on your account. Notify us immediately of any unauthorised use, suspicious behaviour or security breach.',
  'Deve ter pelo menos 16 anos para utilizar este serviço. Utilizadores menores de 18 anos devem ter consentimento dos seus pais ou tutores legais.': 'You must be at least 16 years old to use this service. Users under 18 must have consent from a parent or legal guardian.',
  'Globe Memories é um espaço dedicado à comunidade genuína de viajantes. A nossa missão é permitir que viajantes reais partilhem as suas experiências autênticas, memórias, dicas e recomendações. Valorizamos:': 'Globe Memories is a space for a genuine traveller community. Our mission is to let real travellers share authentic experiences, stories, tips and recommendations. We value:',
  'Mantém a propriedade de todo o conteúdo que carrega, publica ou exibe no Globe Memories. Ao publicar conteúdo, concede-nos uma licença mundial, não exclusiva, para usar, modificar, reproduzir e distribuir esse conteúdo para fins de operação da plataforma.': 'You retain ownership of all content you upload, post or display on Globe Memories. By publishing content, you grant us a worldwide, non-exclusive licence to use, modify, reproduce and distribute it to operate the platform.',
  'Se encontrar conteúdo que viola estes termos, pode denunciá-lo através da função "Denunciar" disponível em cada publicação. A denúncia deve ser específica e descrever exatamente qual é a violação.': 'If you find content that violates these terms, report it using the “Report” function available on each post. Reports must be specific and describe the violation exactly.',
  'Se um utilizador está em violação reiterada destes termos, pode denunciá-lo através da opção "Denunciar Utilizador" no seu perfil. Motivos válidos incluem:': 'If a user repeatedly violates these terms, you can report them using “Report User” on their profile. Valid reasons include:',
  'A nossa equipa de moderação revê todas as denúncias dentro de 24–48 horas. Podemos remover conteúdo, suspender contas ou tomar ação disciplinar conforme apropriado. Utilizadores com comportamento repetidamente inadequado serão permanentemente banidos.': 'Our moderation team reviews all reports within 24–48 hours. We may remove content, suspend accounts or take appropriate disciplinary action. Users who repeatedly behave improperly may be permanently banned.',
  'Concorda em não usar o serviço para:': 'You agree not to use the service to:',
  'O Globe Memories e todo o seu código, design, funcionalidades, conteúdo original e interface são propriedade exclusiva nossa e dos nossos licenciadores, protegidos por leis de direitos de autor.': 'Globe Memories and all of its code, design, features, original content and interface are our exclusive property and that of our licensors, protected by copyright law.',
  'Reservamo-nos o direito de modificar, melhorar ou descontinuar o serviço a qualquer momento, com ou sem aviso prévio.': 'We reserve the right to modify, improve or discontinue the service at any time, with or without notice.',
  'O serviço é fornecido "como está" e "conforme disponível". Não garantimos que o serviço será ininterrupto, seguro ou livre de erros.': 'The service is provided “as is” and “as available”. We do not guarantee that it will be uninterrupted, secure or error-free.',
  'Podemos rescindir ou suspender a sua conta e acesso ao serviço imediatamente, sem aviso prévio, por qualquer motivo, incluindo violação destes termos, comportamento abusivo, autopromoção persistente, múltiplas denúncias comprovadas ou atividades fraudulentas ou ilegais.': 'We may terminate or suspend your account and access immediately, without notice, for any reason including breach of these terms, abusive behaviour, persistent self-promotion, multiple substantiated reports or fraudulent or illegal activity.',
  'Reservamo-nos o direito de alterar estes termos a qualquer momento. Notificaremos sobre alterações significativas através da plataforma ou por email.': 'We reserve the right to change these terms at any time. We will notify you of significant changes through the platform or by email.',
  'Estes termos são regidos pelas leis de Portugal, sem consideração aos seus conflitos de leis. Qualquer disputa será resolvida nos tribunais competentes em Portugal.': 'These terms are governed by the laws of Portugal, without regard to conflicts of law. Any dispute will be resolved by the competent courts in Portugal.',
  'Se tiver questões, preocupações ou desejar reportar violações, pode contactar-nos:': 'If you have questions, concerns or want to report violations, contact us:',
  'Recolhemos informações que nos fornece diretamente durante o registo e utilização da plataforma:': 'We collect information you provide directly during registration and use of the platform:',
  'Quando utiliza o nosso serviço, recolhemos automaticamente:': 'When you use our service, we automatically collect:',
  'Utilizamos as suas informações para:': 'We use your information to:',
  'O conteúdo que escolhe tornar público será visível para outros utilizadores. Pode controlar completamente a privacidade do seu conteúdo nas suas definições.': 'Content you choose to make public will be visible to other users. You can fully control your content privacy in Settings.',
  'Não vendemos, não alugamos e não partilhamos as suas informações pessoais com terceiros para fins de marketing comercial. Isto é fundamental para a confiança na comunidade.': 'We do not sell, rent or share your personal information with third parties for commercial marketing. This is fundamental to community trust.',
  'Podemos partilhar informações com prestadores de serviços terceiros sob rigorosos acordos de confidencialidade:': 'We may share information with third-party service providers under strict confidentiality agreements:',
  'Podemos divulgar informações se exigido por lei ou ordem judicial para proteger direitos, prevenir fraudes ou proteger a segurança de utilizadores.': 'We may disclose information when required by law or court order to protect rights, prevent fraud or protect user safety.',
  'Implementamos medidas de segurança técnicas e organizacionais adequadas:': 'We implement appropriate technical and organisational security measures:',
  'Responsabilidade partilhada: Nenhum método é 100% seguro. Você é responsável pela sua palavra-passe.': 'Shared responsibility: no method is 100% secure. You are responsible for your password.',
  'Utilizamos cookies e tecnologias similares para:': 'We use cookies and similar technologies to:',
  'De acordo com a Lei de Proteção de Dados (RGPD), tem os seguintes direitos:': 'Under the Data Protection Law (GDPR), you have the following rights:',
  'Mantemos as suas informações pessoais apenas pelo tempo necessário para cumprir os fins descritos nesta política, a menos que um período de retenção mais longo seja exigido ou permitido por lei.': 'We keep your personal information only for as long as necessary for the purposes described in this policy, unless a longer retention period is required or permitted by law.',
  'As suas informações podem ser transferidas e processadas em servidores localizados em Portugal ou países parceiros. Garantimos que tais transferências cumprem os requisitos do RGPD.': 'Your information may be transferred to and processed on servers in Portugal or partner countries. We ensure these transfers comply with GDPR requirements.',
  'O nosso serviço não se destina a menores de 16 anos. Não recolhemos intencionalmente informações pessoais de menores de 16 anos. Utilizadores entre 16 e 18 anos podem usar o serviço apenas com consentimento dos pais ou tutores legais.': 'Our service is not intended for children under 16. We do not knowingly collect personal information from children under 16. Users aged 16 to 18 may use the service only with parent or guardian consent.',
  'Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos sobre alterações significativas publicando a nova política no nosso site e por email aos utilizadores ativos.': 'We may update this Privacy Policy periodically. We will notify active users of significant changes by publishing the new policy on our site and by email.',
  'Se tiver questões sobre esta Política de Privacidade, pode contactar-nos:': 'If you have questions about this Privacy Policy, contact us:',
  'Direito a reclamação: Tem o direito de apresentar uma reclamação à autoridade de proteção de dados competente (CNPD em Portugal).': 'Right to complain: you have the right to lodge a complaint with the competent data protection authority (CNPD in Portugal).',
  'Privacidade é um valor fundamental de Globe Memories. Comprometemo-nos a:': 'Privacy is a core value at Globe Memories. We commit to:',
  '⚠️ Ao criar uma conta e utilizar Globe Memories, confirma que leu, compreendeu e aceita integralmente estes Termos e Condições e a Política de Privacidade.': '⚠️ By creating an account and using Globe Memories, you confirm that you have read, understood and fully accept these Terms and Conditions and the Privacy Policy.',
  '🔒 Compromisso Fundamental: Privacidade é um valor fundamental. Nunca vendemos dados a terceiros. Somos transparentes e respeitamos as suas escolhas de privacidade.': '🔒 Core commitment: privacy is a fundamental value. We never sell data to third parties. We are transparent and respect your privacy choices.',
  '🍪 Utilizamos apenas as tecnologias necessárias ou justificadas para disponibilizar uma experiência segura, funcional e transparente aos viajantes.': '🍪 We only use technologies that are necessary or justified to provide travellers with a safe, functional and transparent experience.',
  'Esta Política de Cookies explica como a Globe Memories utiliza cookies e tecnologias semelhantes para garantir que a plataforma funciona, proteger as contas e recordar algumas preferências. Não utilizamos cookies para publicidade comportamental nem vendemos dados para campanhas de terceiros.': 'This Cookie Policy explains how Globe Memories uses cookies and similar technologies to keep the platform working, protect accounts and remember preferences. We do not use cookies for behavioural advertising or sell data for third-party campaigns.',
  'Cookies são pequenos ficheiros guardados no navegador ou no dispositivo quando visita um site. Permitem reconhecer uma sessão, guardar uma preferência e compreender se uma funcionalidade está a funcionar corretamente.': 'Cookies are small files stored in your browser or device when you visit a site. They recognise a session, save a preference and help us understand whether a feature is working correctly.',
  'Para manter a sessão iniciada e guardar preferências no seu dispositivo, a aplicação também pode utilizar armazenamento local ou armazenamento de sessão do navegador. Estes dados ficam associados ao dispositivo e não são utilizados para o seguir noutros sites.': 'To keep you signed in and save preferences, the app may also use local or session storage in your browser. These data stay associated with your device and are not used to track you on other sites.',
  'Algumas funcionalidades, como conteúdos multimédia ou serviços de mapas e meteorologia, podem comunicar com fornecedores técnicos externos. Esses fornecedores podem ter as suas próprias regras de privacidade e cookies. A Globe Memories não autoriza a utilização dos seus dados para publicidade de viagens ou marketing direcionado através da plataforma.': 'Some features, such as multimedia or map and weather services, may communicate with external technical providers. Those providers may have their own privacy and cookie rules. Globe Memories does not authorise use of your data for travel advertising or targeted marketing through the platform.',
  'Pode apagar ou bloquear cookies nas definições do seu navegador. Também pode terminar a sessão e limpar os dados do site a qualquer momento. O bloqueio de cookies essenciais pode impedir o login ou algumas funções de funcionarem corretamente.': 'You can delete or block cookies in your browser settings. You can also sign out and clear site data at any time. Blocking essential cookies may prevent login or some features from working correctly.',
  'O que são cookies?': 'What are cookies?',
  'Que tecnologias podemos utilizar?': 'What technologies may we use?',
  'Armazenamento local e sessões': 'Local storage and sessions',
  'Cookies de terceiros': 'Third-party cookies',
  'Como gerir as suas escolhas': 'How to manage your choices',
  'Atualizações e contacto': 'Updates and contact',
  'Oops! Algo correu mal': 'Oops! Something went wrong',
  'Pedimos desculpa, mas ocorreu um erro inesperado.': 'Sorry, an unexpected error occurred.',
  'A nossa equipa foi notificada e está a trabalhar para resolver o problema.': 'Our team has been notified and is working to resolve the problem.',
  'Tentar Novamente': 'Try again',
  'Detalhes Técnicos (Desenvolvimento)': 'Technical details (development)',
  'Erro:': 'Error:',
  'Stack Trace:': 'Stack trace:',
  'Tentativas de recuperação:': 'Recovery attempts:',
  'A tua conta foi removida. Contacta o suporte para mais informações.': 'Your account was removed. Contact support for more information.',
  'A tua sessão terminou automaticamente. Não podes aceder à plataforma enquanto a conta estiver suspensa.': 'Your session ended automatically. You cannot access the platform while the account is suspended.',
  'ATENÇÃO: Esta ação é irreversível! Tem a certeza?': 'WARNING: This action is irreversible! Are you sure?',
  'Organize viagens colaborativas com amigos e família.': 'Organise collaborative trips with friends and family.',
  'Gestão de Orçamento': 'Budget management',
  'Visualize todo o seu itinerário num mapa dinâmico.': 'View your entire itinerary on a dynamic map.',
  'Esta funcionalidade será lançada em breve com muitas surpresas.': 'This feature will launch soon with many surprises.',
  'Fique atento às próximas atualizações da Globe Memories!': 'Stay tuned for the next Globe Memories updates!',
  'O que está a chegar na Fase 2': 'What is coming in Phase 2',
  'Não mostrar novamente esta mensagem': 'Do not show this message again',
  'Começar a conquistar!': 'Start earning achievements!',
  'Tabela oficial de pontuação da comunidade Globe Memories.': 'Official Globe Memories community leaderboard.',
  'Explore o mundo e partilhe as suas viagens para desbloquear novos troféus.': 'Explore the world and share your trips to unlock new trophies.',
  'Posição': 'Position',
  'Você': 'You',
  'Sistema de Conquistas Globe Memories': 'Globe Memories Achievement System',
  'Transforme as suas aventuras em realizações!': 'Turn your adventures into achievements!',
  'Desbloqueie conquistas, ganhe pontos e suba no ranking global da comunidade de viajantes Globe Memories.': 'Unlock achievements, earn points and climb the Globe Memories traveller community leaderboard.',
  'Conquistas Dinâmicas': 'Dynamic Achievements',
  'Obtenha novas conquistas ao viajar, partilhar fotos, interagir com outros exploradores e completar desafios únicos.': 'Earn new achievements by travelling, sharing photos, interacting with other explorers and completing unique challenges.',
  'Pontuação e Ranking Global': 'Points and Global Leaderboard',
  'Acumule pontos por cada conquista alcançada e veja o seu nome subir na tabela de liderança mundial.': 'Earn points for every achievement and watch your name climb the global leaderboard.',
  'Progresso Visual Personalizado': 'Personalised Visual Progress',
  'Acompanhe o seu desempenho através de gráficos, barras de progresso e estatísticas detalhadas.': 'Track your progress with charts, progress bars and detailed statistics.',
  'Competição Saudável e Motivadora': 'Healthy, Motivating Competition',
  'Compare as suas conquistas com outros viajantes, desafie amigos e mantenha-se sempre inspirado a explorar mais.': 'Compare achievements with other travellers, challenge friends and stay inspired to explore more.',
  'Como ganhar pontos?': 'How to earn points?',
  'Progresso Geral': 'Overall progress',
  'Desbloqueie todos os troféus para ganhar o bónus de': 'Unlock all trophies to earn the bonus of',
  'Completo': 'Complete',
  'Total': 'Total',
  'Desbloqueadas': 'Unlocked',
  'Em Progresso': 'In progress',
  'Bloqueadas': 'Locked',
  'Filtro de conquistas': 'Achievement filter',
  'Procurar conquista…': 'Search achievements…',
  'Procurar conquista': 'Search achievements',
  'Todas as Conquistas': 'All achievements',
  'Sem conquistas neste filtro': 'No achievements in this filter',
  'Leaderboard': 'Leaderboard',
  'pts': 'pts',
  'Enquanto isso, explore as nossas outras funcionalidades': 'Meanwhile, explore our other features',
  'Descobrir Viagens': 'Discover Trips',
  'As Minhas Viagens': 'My Trips',
  'Mapa Mundial': 'World Map',
  'Assistente de Viagem com IA': 'AI Travel Assistant',
  'Planeamento Colaborativo': 'Collaborative Planning',
  'Convide amigos e familiares para planear juntos em tempo real.': 'Invite friends and family to plan together in real time.',
  'Controle todos os custos da sua viagem em tempo real.': 'Track all your trip costs in real time.',

  // Account, feedback and shared UI states
  'Conta removida': 'Account removed',
  'Conta suspensa': 'Account suspended',
  'Ir para o login': 'Go to login',
  'A tua sessão terminou automaticamente porque a tua conta foi removida por um administrador.': 'Your session ended automatically because your account was removed by an administrator.',
  'A tua sessão terminou automaticamente. Não podes aceder à plataforma enquanto a tua conta estiver suspensa.': 'Your session ended automatically. You cannot access the platform while the account is suspended.',
  'Erro no servidor:': 'Server error:',
  'Pronto para explorar! 🎉': 'Ready to explore! 🎉',
  'Reportar erro ou sugerir melhoria': 'Report an issue or suggest an improvement',
  'Ir para a secção X': 'Go to section X',
  'O comentário não pode estar vazio!': 'Comment cannot be empty!',
  'Comentário contém conteúdo inválido!': 'Comment contains invalid content!',
  'Comentário contém conteúdo não permitido!': 'Comment contains disallowed content!',
  'Comentário adicionado com sucesso!': 'Comment added successfully!',
  'Pedir para seguir': 'Request to follow',
  'respondeu à tua pergunta': 'replied to your question',

  // Achievements
  'Partilhe a sua primeira viagem': 'Share your first trip',
  'Amigo de Viagem': 'Travel Friend',
  'Dar gosto numa viagem de outro utilizador': 'Like another user’s trip',
  'Viajante Económico': 'Budget Traveller',
  'Gastar menos de 500 € numa viagem': 'Spend less than €500 on a trip',
  'Publicar uma viagem com mais de 5 fotos': 'Publish a trip with more than 5 photos',
  'Escrever uma descrição com mais de 100 palavras numa viagem': 'Write a trip description with more than 100 words',
  'Seguir alguém de um país diferente do seu': 'Follow someone from a different country',
  'Receber 5 estrelas numa viagem publicada': 'Receive five stars on a published trip',
  'Publicar uma viagem com todas as informações preenchidas': 'Publish a trip with every field completed',
  'Visite 3 países diferentes': 'Visit three different countries',
  'Visitar 10 países diferentes': 'Visit ten different countries',
  'Crítico de Viagens': 'Travel Critic',
  'Viajante Lendário': 'Legendary Traveller',
  'Ter uma viagem com 5 estrelas e pelo menos 20 gostos': 'Have a five-star trip with at least 20 likes',
  'Publicar uma viagem com duração de 2 a 3 dias': 'Publish a trip lasting two to three days',
  'Fazer 5 viagens de avião': 'Take five flights',
  'Visitar 25 países diferentes': 'Visit 25 different countries',
  'Fazer uma viagem com mais de 30 dias de duração': 'Take a trip lasting more than 30 days',
  'Caçador de Tesouros': 'Treasure Hunter',
  'Receber 100 gostos numa única viagem': 'Receive 100 likes on one trip',
  'Criar uma viagem': 'Create a trip',
  '+50 pontos': '+50 points',
  'Receber um gosto numa viagem': 'Receive a like on a trip',
  '+2 pontos': '+2 points',
  '+3 pontos': '+3 points',
  'Comentar a viagem de outro utilizador': 'Comment on another user’s trip',
  '+1 ponto': '+1 point',
  'Gostar de uma viagem de outro utilizador': 'Like another user’s trip',
  'Completar o perfil (bio, foto, etc.)': 'Complete your profile (bio, photo, etc.)',
  '+20 pontos': '+20 points',
  '+15 pontos': '+15 points',
  '+250 pontos': '+250 points',

  // Profile editor
  'Português, Inglês': 'Portuguese, English',
  'Foto de Perfil': 'Profile photo',
  'Arraste para ajustar a capa': 'Drag to adjust the cover',
  'Deixe vazio para manter': 'Leave blank to keep the current value',
  'País e Cidade': 'Country and city',
  'De onde é e onde está agora.': 'Where you are from and where you are now.',
  'País': 'Country',
  'Género': 'Gender',
  'Prefiro não dizer': 'Prefer not to say',
  'Línguas': 'Languages',
  'Línguas que fala': 'Languages you speak',
  'Selecione as línguas no menu...': 'Select languages from the menu…',
  'Tipos de viagem preferidos': 'Preferred trip types',
  'Não tem permissão para editar este perfil.': 'You do not have permission to edit this profile.',
  'A capa não foi actualizada.': 'The cover was not updated.',
  'A capa não foi removida.': 'The cover was not removed.',
  'Dados de acesso atualizados. Inicie sessão novamente.': 'Login details updated. Please log in again.',
  'Formato não suportado.': 'Unsupported format.',
  'A foto aparece no perfil e nas partilhas.': 'The photo appears on your profile and in shares.',
  'A foto actual será removida.': 'The current photo will be removed.',
  'A imagem de cabeçalho aparece no topo do perfil.': 'The header image appears at the top of the profile.',
  'Sem capa definida.': 'No cover set.',
  'A capa actual será removida.': 'The current cover will be removed.',
  'Nova capa seleccionada.': 'New cover selected.',
  'Pode alterar o nome de utilizador uma vez a cada 30 dias.': 'You can change your username once every 30 days.',
  'Selecione ou pesquise a cidade': 'Select or search for a city',

  // Help and FAQ answers
  'Clica em "Registar" no menu principal. Preenche os campos solicitados — nome, email e palavra-passe — e clica em "Registar". Vais receber um email de confirmação para validar a tua conta antes de fazeres login.': 'Click “Sign up” in the main menu. Fill in your name, email and password, then click “Sign up”. You will receive a confirmation email before logging in.',
  'Clica em "Entrar" no menu principal, insere o teu email e palavra-passe. Se esqueceste a palavra-passe, há um link "Esqueci a minha palavra-passe" que te guia na recuperação via email.': 'Click “Log in” in the main menu and enter your email and password. If you forgot your password, use the “Forgot password” link to recover it by email.',
  'Vai a "O Meu Perfil" no menu. Aqui podes atualizar a fotografia, descrição, nacionalidade e outras preferências. Podes também escolher o nível de privacidade (privado ou público) e quem vê as tuas viagens.': 'Go to “My Profile” in the menu. There you can update your photo, description, nationality and other preferences. You can also choose privacy and who can see your trips.',
  'Vai a "As Minhas Viagens" e clica em "Criar Viagem" ou "+". Preenche destino, datas, descrição, fotos/vídeos e tipo de transporte. No fim, clica em "Guardar Viagem".': 'Go to “My Trips” and click “Create Trip” or “+”. Fill in the destination, dates, description, photos/videos and transport. Finally, click “Save Trip”.',
  'Abre a viagem desejada e clica em "Adicionar Memória" ou "+". Podes adicionar descrição, fotos, vídeos, data e localização GPS. Cada memória fica georreferenciada, criando um mapa pessoal das tuas aventuras.': 'Open the trip and click “Add Story” or “+”. You can add a description, photos, videos, date and GPS location. Each story is geotagged on your personal adventure map.',
  'São troféus digitais que desbloqueias ao atingir marcos — número de países, memórias, sequência de dias ativos, etc. Vê todas as tuas conquistas em "Conquistas" no teu perfil.': 'Achievements are digital trophies unlocked at milestones such as countries, stories and active-day streaks. See them in “Achievements” on your profile.',
  'Vai a "Definições e Privacidade". Aqui defines se o perfil é público ou privado, quem vê as tuas viagens, quem comenta, notificações, e podes bloquear utilizadores específicos.': 'Go to “Settings and privacy”. There you choose profile and trip visibility, comments and notifications, and can block specific users.',
  'Inicie sessão para criar uma pergunta!': 'Log in to ask a question!',
  'Escreva uma pergunta!': 'Write a question!',
  'A pergunta deve ter pelo menos 10 caracteres!': 'The question must have at least 10 characters!',
  'A pergunta deve ter no máximo 500 caracteres!': 'The question must be at most 500 characters!',
  'Pergunta contém conteúdo não permitido!': 'Question contains disallowed content!',
  'Pergunta contém conteúdo perigoso que foi removido!': 'Question contained dangerous content that was removed!',
  'Pergunta criada com sucesso!': 'Question created successfully!',
  'Erro ao criar pergunta.': 'Error creating question.',
  'Erro ao processar gosto.': 'Error processing like.',
  'Tem a certeza que quer eliminar esta pergunta?': 'Are you sure you want to delete this question?',
  'Pergunta eliminada.': 'Question deleted.',
  'Erro ao eliminar pergunta.': 'Error deleting question.',
  'Fazer Pergunta': 'Ask a question',
  'Tente ajustar os filtros ou fazer uma nova pesquisa.': 'Try adjusting the filters or starting a new search.',
  'Página anterior': 'Previous page',
  'Próxima página': 'Next page',

  // Notifications and permission feedback
  'Viagem adicionada ao teu perfil.': 'Trip added to your profile.',
  'Quando alguém gostar das tuas viagens, comentar ou começar a seguir-te, aparece aqui.': 'When someone likes your trips, comments or follows you, it will appear here.',
  'Erro ao carregar utilizadores bloqueados:': 'Error loading blocked users:',
  'Erro ao atualizar privacidade.': 'Error updating privacy.',
  'Erro ao carregar sessões:': 'Error loading sessions:',
  'Erro ao desbloquear.': 'Error unblocking.',
  'Erro ao preparar dados.': 'Error preparing data.',

  // Shared travel-detail, gallery and comment controls
  'Opções de denúncia': 'Report options',
  'Inicie sessão para gostar.': 'Log in to like this trip.',
  'Não foi possível processar o gosto.': 'Could not process the like.',
  'Não foi possível criar o link de partilha.': 'Could not create the share link.',
  'Link copiado para a área de transferência.': 'Link copied to the clipboard.',
  'Não pode denunciar as suas próprias viagens.': 'You cannot report your own trips.',
  'Não foi possível identificar o viajante.': 'Could not identify the traveller.',
  'Não pode denunciar o seu próprio perfil.': 'You cannot report your own profile.',
  'Inicia sessão para veres esta viagem': 'Log in to see this trip',
  'Segue este viajante para veres a viagem': 'Follow this traveller to see the trip',
  'Esta viagem é privada': 'This trip is private',
  'Não foi possível carregar esta viagem. Tente novamente.': 'Could not load this trip. Try again.',
  'Não foi possível carregar os dados desta viagem. Tente novamente mais tarde.': 'Could not load this trip data. Try again later.',
  'Pontos de Referência': 'Points of interest',
  'Filtrar galeria por localização': 'Filter gallery by location',
  'Abrir vídeo associado ao alojamento': 'Open video linked to the accommodation',
  'Abrir vídeo associado à alimentação': 'Open video linked to the food recommendation',
  'Ocultar comentários': 'Hide comments',
  'Mostrar comentários': 'Show comments',
  'Adicionar comentário...': 'Add a comment…',
  'Publicar comentário': 'Post comment',
  'Enviar resposta': 'Send reply',
  'Reações rápidas': 'Quick reactions',
  'Fechar comentários': 'Close comments',
  'Comentários da viagem': 'Trip comments',
  'Ainda sem comentários. Sê o primeiro a comentar!': 'No comments yet. Be the first to comment!',
  '0 comentários': '0 comments',
  'comentário': 'comment',
  'Vídeo': 'Video',
  'Conteúdo geral da viagem': 'General trip content',
  'item sem destino': 'item without destination',
  'itens sem destino': 'items without destination',
  'dia': 'day',
  'dias': 'days',
  'Ações': 'Actions',
  'Avaliação e preço': 'Rating and price',
  'Categorias e tipo de destino': 'Categories and destination type',
  'Cidade, País': 'City, Country',
  'Foto anterior': 'Previous photo',
  'Próxima foto': 'Next photo',
  'Lista com marcadores': 'Bulleted list',

  // Notifications and push permission prompt
  'Aceitar viagem': 'Accept trip',
  'gostou do teu comentário': 'liked your comment',
  'Pedido aceite.': 'Request accepted.',
  'Pedido rejeitado.': 'Request rejected.',
  'Permitir notificações?': 'Allow notifications?',
  'Recebe alertas de novos comentários, gostos, seguidores e mensagens importantes, mesmo quando não tens o Globe Memories aberto.': 'Receive alerts about new comments, likes, followers and important messages, even when Globe Memories is not open.',
  'A ativar…': 'Enabling…',
  'Permitir notificações': 'Allow notifications',

  // First-trip tutorial copy
  'Estas escolhas permitem que outras pessoas encontrem viagens com interesses semelhantes.': 'These choices help other people find trips with similar interests.',
  'Escolhe o tipo de local e associa-o ao destino certo.': 'Choose the place type and link it to the right destination.',
  'Organiza as atividades dentro de cada dia. Em viagens multidestino, os destinos aparecem automaticamente.': 'Organise activities within each day. On multi-destination trips, destinations appear automatically.',
  'Podes escrever legendas e associar cada ficheiro a um destino ou local.': 'You can write captions and link each file to a destination or place.',

  // Profile photo uploader and image controls
  'Pré-visualização da foto de perfil': 'Profile photo preview',
  'Arraste para ajustar': 'Drag to adjust',
  'Escolher foto de perfil': 'Choose profile photo',
  'Adicionar foto': 'Add photo',
  'Trocar foto': 'Change photo',
  'A preparar…': 'Preparing…',

  // Coming-soon and future-trip page
  'Crie itinerários detalhados com sugestões personalizadas.': 'Create detailed itineraries with personalised suggestions.',

  // Weather and shared accessibility labels
  'Erro ao obter sugestões:': 'Error getting suggestions:',
  'Erro ao obter dados meteorológicos:': 'Error getting weather data:',
  'A previsão atualizada está temporariamente indisponível. A mostrar os últimos dados guardados.': 'The updated forecast is temporarily unavailable. Showing the last saved data.',
  'Erro ao buscar meteorologia:': 'Error fetching weather:',
  'Erro ao carregar meteorologia.': 'Error loading weather.',
  'Coordenadas inválidas para a cidade selecionada.': 'Invalid coordinates for the selected city.',
  'Dar like a esta viagem': 'Like this trip',
  'Partilhar esta viagem': 'Share this trip',
  'Comentar esta viagem': 'Comment on this trip',
  'Denunciar conteúdo inadequado': 'Report inappropriate content',
  'Imagem de viagem': 'Trip image',
  'Conteúdo complementar': 'Complementary content',
  'Não foi possível concluir esta operação. Tente novamente.': 'Could not complete this operation. Try again.',
  'A sessão expirou. Inicia sessão novamente.': 'Your session expired. Log in again.',
  'Não tens autorização para realizar esta ação.': 'You are not authorised to perform this action.',
  'Não foi possível encontrar o recurso solicitado.': 'The requested resource could not be found.',
  'Não foi possível concluir porque os dados já foram alterados.': 'Could not complete because the data has already changed.',
  'O ficheiro excede o limite permitido. Vídeos: 200 MB; imagens: 20 MB.': 'The file exceeds the allowed limit. Videos: 200 MB; images: 20 MB.',
  'Há demasiados pedidos neste momento. Tenta novamente daqui a pouco.': 'There are too many requests right now. Try again shortly.',
  'As notificações foram bloqueadas no navegador. Ativa-as nas definições do site.': 'Notifications were blocked by the browser. Enable them in the site settings.',
  'A autorização das notificações foi cancelada.': 'Notification permission was cancelled.',
  'As notificações push ainda não estão disponíveis.': 'Push notifications are not available yet.',
  'A subscrição push devolvida pelo navegador é inválida.': 'The push subscription returned by the browser is invalid.',
  'Conta suspensa: <reason>': 'Account suspended: <reason>',
  'ErrorBoundary capturou um erro:': 'ErrorBoundary caught an error:',
  'Por favor escolha uma imagem JPG, PNG, WEBP ou HEIC.': 'Please choose a JPG, PNG, WEBP or HEIC image.',
  '+10 pontos': '+10 points',
  'Receber um comentário numa viagem': 'Receive a comment on a trip',
  'O nome de utilizador deve ter entre 3 e 20 caracteres.': 'Username must be between 3 and 20 characters.',
  'Bio demasiado longa (máx. 500 caracteres).': 'Bio is too long (max. 500 characters).',
  'Cidade demasiado longa (máx. 100).': 'City is too long (max. 100 characters).',
  'Introduza a nova palavra-passe.': 'Enter the new password.',
  'Foto de Capa': 'Cover photo',
  'Substituir imagem': 'Replace image',
  'O seu nome de utilizador': 'Your username',
  'Ocultar nova palavra-passe': 'Hide new password',
  'Mostrar nova palavra-passe': 'Show new password',
  'Repita a nova palavra-passe': 'Repeat the new password',
  'Tens uma ideia para melhorar a plataforma': 'Have an idea to improve the platform',
  'Erro ao enviar mensagem. Tenta novamente.': 'Error sending message. Try again.',
  'Página não encontrada · Globe Memories': 'Page not found · Globe Memories',
  'Erro ao carregar perguntas:': 'Error loading questions:',
  'Erro ao carregar perguntas.': 'Error loading questions.',
  'Erro ao carregar as minhas perguntas:': 'Error loading my questions:',
  'Erro ao carregar as suas perguntas.': 'Error loading your questions.',
  'Erro ao criar pergunta:': 'Error creating question:',
  'Erro ao adicionar comentário:': 'Error adding comment:',
  'Erro ao eliminar pergunta:': 'Error deleting question:',
  'Erro ao eliminar comentário:': 'Error deleting comment:',
  'O primeiro nome deve ter pelo menos 2 caracteres': 'First name must have at least 2 characters',
  'O primeiro nome deve conter apenas letras': 'First name may contain letters only',
  'O último nome é obrigatório': 'Last name is required',
  'O último nome deve ter pelo menos 2 caracteres': 'Last name must have at least 2 characters',
  'O último nome é demasiado longo': 'Last name is too long',
  'O último nome deve conter apenas letras': 'Last name may contain letters only',
  'O nome de utilizador deve ter pelo menos 3 caracteres': 'Username must have at least 3 characters',
  'A palavra-passe é obrigatória': 'Password is required',
  'A palavra-passe deve ter pelo menos 8 caracteres': 'Password must have at least 8 characters',
  'A palavra-passe é demasiado longa': 'Password is too long',
  'A palavra-passe deve conter pelo menos uma letra minúscula': 'Password must contain at least one lowercase letter',
  'A palavra-passe deve conter pelo menos uma letra maiúscula': 'Password must contain at least one uppercase letter',
  'A palavra-passe deve conter pelo menos um dígito': 'Password must contain at least one digit',
  'A palavra-passe deve ter pelo menos 8 caracteres.': 'Password must have at least 8 characters.',
  'A palavra-passe deve conter pelo menos uma letra maiúscula.': 'Password must contain at least one uppercase letter.',
  'A palavra-passe deve conter pelo menos uma letra minúscula.': 'Password must contain at least one lowercase letter.',
  'A palavra-passe deve conter pelo menos um número.': 'Password must contain at least one number.',
  'Token de recuperação não encontrado. Solicite um novo link de recuperação.': 'Recovery token not found. Request a new recovery link.',
  'Token inválido ou expirado. Solicite um novo link de recuperação.': 'Invalid or expired token. Request a new recovery link.',
  'Não foi possível validar o link. Solicite um novo email de recuperação.': 'Could not validate the link. Request a new recovery email.',
  'Nova palavra-passe é obrigatória.': 'New password is required.',
  'Palavra-passe alterada! Redirecionando para o login...': 'Password changed! Redirecting to login…',
  'Palavra-passe alterada com sucesso! Inicie sessão.': 'Password changed successfully! Log in.',
  'Erro ao alterar palavra-passe. Tente novamente.': 'Error changing password. Try again.',
  'Token recebido por email': 'Token received by email',
  'Crie uma palavra-passe forte': 'Create a strong password',
  'Esconder palavra-passe': 'Hide password',
  'Repita a palavra-passe': 'Repeat the password',
  'Os seus dados foram preparados.': 'Your data has been prepared.',
  'Confirme novamente que pretende eliminar a conta.': 'Confirm again that you want to delete the account.',
  'Fecha sessão em todos os dispositivos': 'Sign out on all devices',
  'Visibilidade do Perfil': 'Profile visibility',
  'Define quem pode ver o teu perfil': 'Choose who can see your profile',
  'Visibilidade do perfil': 'Profile visibility',
  'A guardar...': 'Saving…',
  'Guardar alterações': 'Save changes',
  'Terminar sessão em todos os dispositivos': 'Sign out on all devices',
  'Ocorreu um erro inesperado.': 'An unexpected error occurred.',
  'Libéria': 'Liberia',
  'Micronésia': 'Micronesia',
  'Viagens inventadas, perfis falsos, dados incorretos.': 'Invented trips, fake profiles or inaccurate data.',
  'Spam ou autopromoção': 'Spam or self-promotion',
  'Publicidade excessiva, links externos, promoção constante.': 'Excessive advertising, external links or constant promotion.',
  'Uso de fotos ou informações de outra pessoa sem autorização.': 'Using another person’s photos or information without authorisation.',
  'Pesquisa não pode exceder 50 caracteres.': 'Search cannot exceed 50 characters.',
  'Pesquisa contém caracteres não permitidos que foram removidos.': 'Search contained disallowed characters, which were removed.',
  'Inicie sessão para denunciar viajantes.': 'Log in to report travellers.',
  'Erro ao descobrir viajantes:': 'Error discovering travellers:',
  'Não foi possível carregar os viajantes neste momento.': 'Could not load travellers right now.',
  'Erro ao verificar estado de seguimento:': 'Error checking follow status:',
  'Erro ao bloquear:': 'Error blocking:',
  'Erro ao obter nome da cidade:': 'Error getting the city name:',
  'Coordenadas inválidas no histórico.': 'Invalid coordinates in history.',
  'Erro ao verificar estado do modal:': 'Error checking modal state:',
  'Erro ao salvar estado do modal:': 'Error saving modal state:',
  'Erro ao resetar modais:': 'Error resetting modals:',
  'O navegador devolveu uma subscrição push inválida.': 'The browser returned an invalid push subscription.',
  // Discovery, authentication and landing-page controls
  'Qualquer mês': 'Any month',
  'Janeiro': 'January',
  'Fevereiro': 'February',
  'Março': 'March',
  'Abril': 'April',
  'Maio': 'May',
  'Junho': 'June',
  'Julho': 'July',
  'Agosto': 'August',
  'Setembro': 'September',
  'Outubro': 'October',
  'Novembro': 'November',
  'Dezembro': 'December',
  'Mais recente': 'Most recent',
  'Mais antigo': 'Oldest',
  'Preço (crescente)': 'Price (lowest first)',
  'Preço (decrescente)': 'Price (highest first)',
  'Avaliação (maior)': 'Rating (highest)',
  'Duração (mais longo)': 'Duration (longest)',
  'Duração (mais curto)': 'Duration (shortest)',
  'Todos os países': 'All countries',
  'A carregar cidades…': 'Loading cities…',
  'Selecione primeiro um país': 'Select a country first',
  'Todas as cidades': 'All cities',
  'Mês': 'Month',
  'Avaliação mínima': 'Minimum rating',
  'Preço': 'Price',
  'Mín.': 'Min.',
  'Máx.': 'Max.',
  'Preço mínimo': 'Minimum price',
  'Preço máximo': 'Maximum price',
  'Duração (dias)': 'Duration (days)',
  'Duração mínima (dias)': 'Minimum duration (days)',
  'Duração máxima (dias)': 'Maximum duration (days)',
  'Limpar todos os filtros': 'Clear all filters',
  'Modo de descoberta': 'Discovery mode',
  'Procurar viajantes…': 'Search travellers…',
  'Procurar viajantes': 'Search travellers',
  'Filtrar por estilo de viagem': 'Filter by travel style',
  'Nenhum viajante encontrado': 'No traveller found',
  'Tente outro nome de utilizador ou outra nacionalidade.': 'Try another username or nationality.',
  'Chegou ao fim': 'You have reached the end',
  'Bloquear viajante': 'Block traveller',
  'Fechar filtros': 'Close filters',
  'Em Breve': 'Coming soon',
  'Viaje · Explore · Lembre': 'Travel · Explore · Remember',
  'Feito por viajantes, para viajantes': 'Made by travellers, for travellers',
  'partilhado por': 'shared by',
  'ou': 'or',
  'Não tens conta?': 'Don’t have an account?',
  'Já tem conta?': 'Already have an account?',
  'Os seus dados': 'Your details',
  'Nenhum resultado encontrado': 'No results found',
  'Sugestões:': 'Suggestions:',
  'Pelo menos 8 caracteres': 'At least 8 characters',
  'Uma letra maiúscula': 'One uppercase letter',
  'Uma letra minúscula': 'One lowercase letter',
  'Um número': 'One number',
  'Um carácter especial (!@#$%^&*)': 'One special character (!@#$%^&*)',
  'Explorar comunidade': 'Explore community',
  'Explorar mapa': 'Explore map',
  'Explorador de Viagens Globe Memories': 'Globe Memories Trip Explorer',
  'Descubra experiências de viagem reais partilhadas pela comunidade global.': 'Discover real trip experiences shared by the global community.',
  'Não mostrar novamente': 'Do not show again',
  'Destino encontrado': 'Destination found',
  'Queres explorar viagens neste destino?': 'Would you like to explore trips in this destination?',
  'Progresso de descoberta': 'Discovery progress',
  'Mundo explorado': 'World explored',
  'Fonte': 'Source',
  'Controlos do mapa': 'Map controls',
  'Recentrar mapa': 'Recenter map',
  'Recolher painel': 'Collapse panel',
  'Expandir painel': 'Expand panel',
  'Minhas': 'Mine',
  'Comunidade': 'Community',
  'Pesquisar no mapa': 'Search the map',
  // Trip wizard labels, fields and review cards
  'A carregar viagem': 'Loading trip',
  'Acesso negado': 'Access denied',
  'Rascunho recuperado': 'Draft recovered',
  'A tua criação foi restaurada neste dispositivo. Podes continuar exatamente onde ficaste.': 'Your draft was restored on this device. You can continue where you left off.',
  'Começar de novo': 'Start over',
  'Privacidade da viagem': 'Trip privacy',
  'Fechar wizard': 'Close wizard',
  'Progresso': 'Progress',
  'Imagem ou vídeo de capa': 'Cover image or video',
  'Obrigatório para continuar': 'Required to continue',
  'Obrigatório': 'Required',
  'Adicionar imagem ou vídeo de capa': 'Add a cover image or video',
  'Obrigatório para continuar · arrasta ou clica para escolher': 'Required to continue · drag or click to choose',
  'Nome da viagem': 'Trip name',
  'Ex.: Lisboa ao Porto em 6 dias': 'E.g. Lisbon to Porto in 6 days',
  'Ex.: Uma roadtrip costeira com paragens em Sintra, Ericeira e Peniche.': 'E.g. A coastal road trip with stops in Sintra, Ericeira and Peniche.',
  'Avaliação': 'Rating',
  'Pesquisar país…': 'Search country…',
  'Se não aparecer na lista, escreve o nome e escolhe “Adicionar”.': 'If it does not appear in the list, type the name and choose “Add”.',
  'Data de início': 'Start date',
  'Data de fim': 'End date',
  'Data da reserva (opcional)': 'Booking date (optional)',
  'Custo de voo ou transporte': 'Flight or transport cost',
  'Custo de alojamento': 'Accommodation cost',
  'Custo de extras': 'Extra costs',
  'Moeda': 'Currency',
  'Como foi calculado o custo': 'How the cost was calculated',
  'Custo calculado para': 'Cost calculated for',
  'Custo individual ou partilhado': 'Individual or shared cost',
  'Número de pessoas': 'Number of people',
  'Tudo o que ajuda outros viajantes. Categorias e idiomas utilizados na viagem são obrigatórios; alojamento, transportes, alimentação, locais e extras são opcionais.': 'Everything that helps other travellers. Trip categories and languages are required; accommodation, transport, food, places and extras are optional.',
  'Categorias da viagem': 'Trip categories',
  'Ajuda outros viajantes.': 'Help other travellers.',
  'Onde ficaste hospedado.': 'Where you stayed.',
  'Selecionar…': 'Select…',
  'Destino': 'Destination',
  'Tipo': 'Type',
  'Regime': 'Board basis',
  'noite': 'night',
  'noites': 'nights',
  'Check-in': 'Check-in',
  'Check-out': 'Check-out',
  'Comida recomendada': 'Recommended food',
  'Pratos que provaste e recomendas. Podes indicar também o restaurante.': 'Dishes you tried and recommend. You can also add the restaurant.',
  'Restaurante sugerido (opcional)': 'Suggested restaurant (optional)',
  'Transporte': 'Transport',
  'Meios de transporte.': 'Means of transport.',
  'Destinos *': 'Destinations *',
  'Ex.: Voo Lisboa-Paris': 'E.g. Lisbon–Paris flight',
  'Pontos de referência': 'Points of interest',
  'Locais que visitaste.': 'Places you visited.',
  'Nome *': 'Name *',
  'Tipo de ponto': 'Point type',
  'Destino (opcional)': 'Destination (optional)',
  'Descrição': 'Description',
  'Pontos negativos (opcional)': 'Drawbacks (optional)',
  'O que correu menos bem.': 'What did not go so well.',
  'Título *': 'Title *',
  'Ex.: Mau tempo': 'E.g. Bad weather',
  'Pontos positivos (opcional)': 'Highlights (optional)',
  'O que correu bem na viagem.': 'What went well on the trip.',
  'Ex.: Hospitalidade local': 'E.g. Local hospitality',
  'Usar como capa': 'Use as cover',
  'Usar vídeo como capa': 'Use video as cover',
  'Pesquisar viajante': 'Search traveller',
  'Resultados da pesquisa': 'Search results',
  'Sem foto de capa': 'No cover photo',
  'avaliação': 'rating',
  'Comida': 'Food',
  'Idiomas utilizados na viagem': 'Languages used on the trip',

  // Remaining non-administrative interface copy
  'Denunciar Utilizador': 'Report User',
  'como está': 'as is',
  'conforme disponível': 'as available',
  'Pedido': 'Request',
  'Mais antigas': 'Older',
  'Ver menos': 'See less',
  '[Alimentação: <name>]': '[Food: <name>]',
  'Erro ao carregar comentários:': 'Error loading comments:',
  'Erro ao carregar viagem:': 'Error loading trip:',
  'Não tens permissão para ver esta viagem.': 'You do not have permission to view this trip.',
  'Sem items': 'No items',
  '🍽️ Alimentação': '🍽️ Food',
  '📍 Pontos de Referência': '📍 Points of interest',
  '📅 Itinerário': '📅 Itinerary',
  'Partilha onde ficaste': 'Share where you stayed',
  'Explica como te deslocaste': 'Explain how you got around',
  'Escolhe o meio de transporte e acrescenta o percurso.': 'Choose the transport method and add the route.',
  'Adiciona praias, cascatas, museus, monumentos e outros lugares visitados.': 'Add beaches, waterfalls, museums, monuments and other places you visited.',
  'Adiciona e ordena atividades dentro do dia correspondente.': 'Add and order activities within the corresponding day.',
  'Por fazer': 'To do',
  'Utilizador': 'User',
  'Ver mais comentários': 'View more comments',
  'Ver': 'View',
  'este comentário': 'this comment',
  'Escreva um comentário': 'Write a comment',
  'este viajante': 'this traveller',
  'Descobrir viagens': 'Discover trips',
  'Explorar': 'Explore',
  'Tu': 'You',
  'App': 'App',
  'Contacto': 'Contact',
  'Subscrever newsletter': 'Subscribe to newsletter',
  'Recebe inspiração de viagem semanal.': 'Receive weekly travel inspiration.',
  'Subscrever': 'Subscribe',
  'Feito com amor em Portugal': 'Made with love in Portugal',
  'Pesquisar viagens, lugares ou viajantes': 'Search trips, places or travellers',
  'Selecione pelo menos um motivo.': 'Select at least one reason.',
  'este utilizador': 'this user',
  'Outro (especificar)': 'Other (specify)',
  'Descreva o motivo...': 'Describe the reason…',
  'Descreva o motivo da denúncia…': 'Describe the reason for the report…',
  'Por que deseja denunciar o viajante ': 'Why do you want to report traveller ',
  '? Esta ação reporta o utilizador aos administradores.': '? This action reports the user to the administrators.',
  'Por que deseja denunciar a publicação de ': 'Why do you want to report the post by ',
  '? Esta ação reporta a publicação aos administradores.': '? This action reports the post to the administrators.',
  'Obrigado pelo teu feedback!': 'Thank you for your feedback!',
  'Não foi possível enviar o feedback. Tenta novamente.': 'Could not send the feedback. Try again.',
  'Não foi possível ativar as notificações.': 'Could not enable notifications.',
  'Não foi possível pesquisar viajantes. Tenta novamente.': 'Could not search travellers. Try again.',
  'Pesquisa não pode exceder 100 caracteres.': 'Search cannot exceed 100 characters.',
  'Pesquisa contém caracteres não permitidos.': 'Search contains disallowed characters.',
  'Estilo de viagem': 'Travel style',
  'Pesquisar viagens': 'Search trips',
  'Pesquisar…': 'Search…',
  'Filtrar viajantes por estilo de viagem': 'Filter travellers by travel style',
  'viajante': 'traveller',
  'Utilizador bloqueado.': 'User blocked.',
  'Desbloquear viajante': 'Unblock traveller',
  'FAQ e contacto com a equipa': 'FAQ and team contact',
  'Não foi possível carregar a viagem para edição.': 'Could not load the trip for editing.',
  'Não foi possível guardar a conclusão do tutorial. Será tentado novamente mais tarde.': 'Could not save the tutorial completion. It will be tried again later.',
  'Seleciona um país para continuar.': 'Select a country to continue.',
  'É obrigatória uma imagem ou um vídeo de capa para continuar.': 'A cover image or video is required to continue.',
  'Nome da viagem: preenche este campo para continuar.': 'Trip name: fill in this field to continue.',
  'Usa pelo menos 3 caracteres.': 'Use at least 3 characters.',
  'Nome da viagem: usa pelo menos 3 caracteres.': 'Trip name: use at least 3 characters.',
  'Preenche uma descrição curta para continuar.': 'Fill in a short description to continue.',
  'Descrição curta: preenche este campo para continuar.': 'Short description: fill in this field to continue.',
  'Usa pelo menos 10 caracteres.': 'Use at least 10 characters.',
  'Descrição curta: usa pelo menos 10 caracteres.': 'Short description: use at least 10 characters.',
  'Preenche a descrição completa para continuar.': 'Fill in the full description to continue.',
  'Descrição completa: preenche este campo para continuar.': 'Full description: fill in this field to continue.',
  'Usa pelo menos 20 caracteres.': 'Use at least 20 characters.',
  'Descrição completa: usa pelo menos 20 caracteres.': 'Full description: use at least 20 characters.',
  'Seleciona uma avaliação entre 1 e 5 estrelas.': 'Select a rating between 1 and 5 stars.',
  'Avaliação: seleciona entre 1 e 5 estrelas para continuar.': 'Rating: select between 1 and 5 stars to continue.',
  'viagens que já aconteceram': 'trips that have already happened',
  'Seleciona a data de início para continuar.': 'Select the start date to continue.',
  'Data de início: seleciona uma data para continuar.': 'Start date: select a date to continue.',
  'Seleciona a data de fim para continuar.': 'Select the end date to continue.',
  'Data de fim: seleciona uma data para continuar.': 'End date: select a date to continue.',
  'Fim não pode ser anterior ao início.': 'The end cannot be before the start.',
  'A data de fim não pode ser anterior à data de início.': 'The end date cannot be before the start date.',
  'Início não pode ser no futuro.': 'The start cannot be in the future.',
  'A data de início não pode ser no futuro (viagens que já aconteceram).': 'The start date cannot be in the future (trips that have already happened).',
  'Fim não pode ser no futuro.': 'The end cannot be in the future.',
  'A data de fim não pode ser no futuro (viagens que já aconteceram).': 'The end date cannot be in the future (trips that have already happened).',
  'Custo de voo / transporte': 'Flight / transport cost',
  'Custo total': 'Total cost',
  'Indica pelo menos um custo da viagem.': 'Enter at least one trip cost.',
  'Custos: preenche pelo menos um valor em Voo, Alojamento, Alimentação ou Extras.': 'Costs: enter at least one value for Flight, Accommodation, Food or Extras.',
  'Indica entre 2 e 1.000 pessoas para um custo partilhado.': 'Enter between 2 and 1,000 people for a shared cost.',
  'Seleciona pelo menos uma categoria para continuar.': 'Select at least one category to continue.',
  'Categorias: seleciona pelo menos uma para continuar.': 'Categories: select at least one to continue.',
  'Seleciona pelo menos um idioma utilizado na viagem para continuar.': 'Select at least one language used on the trip to continue.',
  'Idiomas utilizados na viagem: seleciona pelo menos um para continuar.': 'Languages used on the trip: select at least one to continue.',
  'Formato não suportado. Escolhe JPG, PNG, WEBP, HEIC, MP4, WEBM, MOV, AVI ou MKV.': 'Unsupported format. Choose JPG, PNG, WEBP, HEIC, MP4, WEBM, MOV, AVI or MKV.',
  'Não foi possível carregar a capa. Seleciona o ficheiro novamente.': 'Could not load the cover. Select the file again.',
  'Adiciona os vídeos um de cada vez. Apenas o primeiro vídeo desta seleção foi carregado.': 'Add videos one at a time. Only the first video in this selection was loaded.',
  'Rascunho removido. Podes começar uma viagem nova.': 'Draft removed. You can start a new trip.',
  'A imagem ou vídeo de capa não foi carregado.': 'The cover image or video was not loaded.',
  'Não foi possível carregar todos os ficheiros. Nenhuma alteração foi publicada.': 'Could not load all files. No changes were published.',
  'A API não devolveu o identificador da viagem.': 'The API did not return the trip identifier.',
  'A viagem foi guardada, mas não foi possível remover uma identificação.': 'The trip was saved, but an identification could not be removed.',
  'A viagem foi guardada, mas não foi possível enviar todos os pedidos de identificação.': 'The trip was saved, but not all identification requests could be sent.',
  'Não foi possível atualizar a viagem. Tenta novamente.': 'Could not update the trip. Try again.',
  'Não foi possível publicar a viagem. Tenta novamente.': 'Could not publish the trip. Try again.',
  'A publicação foi interrompida.': 'Publishing was interrupted.',
  'Dá identidade à viagem: capa, nome, descrição e avaliação.': 'Give the trip its identity: cover, name, description and rating.',
  'Identifica quem fez esta viagem contigo. Cada pessoa recebe um pedido e decide se quer aceitar.': 'Identify who took this trip with you. Each person receives a request and decides whether to accept.',
  'Tudo pronto? Revê a pré-visualização e publica.': 'All set? Review the preview and publish.',
  'A processar a viagem…': 'Processing the trip…',
  '📍 Um destino': '📍 One destination',
  '+ Adicionar alojamento': '+ Add accommodation',
  '+ Adicionar transporte': '+ Add transport',
  'fotos': 'photos',
  'vídeos': 'videos',
  'Escreve pelo menos 2 caracteres.': 'Enter at least 2 characters.',
  'recomendação': 'recommendation',
  'recomendações': 'recommendations',
  'dias planeados': 'planned days',
  'Todos podem ver': 'Everyone can see it',
  'viagem pública': 'public trip',
  'viagens públicas': 'public trips',
  'As viagens públicas deste viajante aparecem aqui.': 'This traveller’s public trips appear here.',
  'Mapa das viagens públicas': 'Map of public trips',
  '0 viagens': '0 trips',
  'Não foi possível carregar as viagens guardadas.': 'Could not load saved trips.',
  'Total de Viagens': 'Total Trips',
  'A carregar perfil': 'Loading profile',
  'Erro ao buscar perfil:': 'Error fetching profile:',
  'Aguarde — o perfil ainda está a carregar.': 'Please wait — the profile is still loading.',
  'Selecione pelo menos um motivo para denunciar.': 'Select at least one reason to report.',
  'Reportado a partir do perfil do utilizador.': 'Reported from the user profile.',
  'Erro ao denunciar utilizador:': 'Error reporting user:',
  'Erro ao bloquear utilizador:': 'Error blocking user:',
  'Não foi possível bloquear este utilizador.': 'Could not block this user.',
  'Viagem eliminada.': 'Trip deleted.',
  'Não foi possível eliminar a viagem.': 'Could not delete the trip.',
  'Viagem denunciada.': 'Trip reported.',
  'Utilizador desbloqueado.': 'User unblocked.',
  'Erro ao desbloquear utilizador:': 'Error unblocking user:',
  'Não foi possível desbloquear este utilizador.': 'Could not unblock this user.',
  'Erro ao buscar lista:': 'Error loading list:',
  'SEGUIDORES': 'FOLLOWERS',
  'Não foi possível remover o seguidor.': 'Could not remove the follower.',
  'Avaliação guardada.': 'Rating saved.',
  'Não foi possível guardar a avaliação.': 'Could not save the rating.',
  'Não foi possível carregar este perfil.': 'Could not load this profile.',
  'Aguarde até que o viajante aceite o seu pedido.': 'Wait for the traveller to accept your request.',
  'Siga o viajante para ver as viagens e estatísticas.': 'Follow the traveller to see trips and statistics.',
  'Bloquear utilizador': 'Block user',
  'Criar viagem': 'Create trip',
  'Partilha a tua primeira memória para começar.': 'Share your first story to get started.',
  'Reputação do viajante': 'Traveller reputation',
  'Avaliar viajante': 'Rate traveller',
  'Eliminar esta viagem? Esta ação não pode ser revertida.': 'Delete this trip? This action cannot be undone.',
  'Eliminar viagem': 'Delete trip',
  'Canvas indisponível': 'Canvas unavailable',
  'PERFIL DE VIAJANTE': 'TRAVELLER PROFILE',
  'Perfil Globe Memories': 'Globe Memories profile',
  'ESTILO DE VIAGEM PREFERIDO': 'PREFERRED TRAVEL STYLE',
  'Ainda não definido': 'Not set yet',
  'VIAGEM PUBLICADA': 'PUBLISHED TRIP',
  'VIAGENS PUBLICADAS': 'PUBLISHED TRIPS',
  'PERFIL PRIVADO': 'PRIVATE PROFILE',
  'PERFIL PÚBLICO': 'PUBLIC PROFILE',
  'Não foi possível criar a imagem': 'Could not create the image',
  'Ligação copiada.': 'Link copied.',
  'Não foi possível copiar a ligação.': 'Could not copy the link.',
  'A ligação foi copiada. Pode colá-la numa rede social ou numa mensagem.': 'The link was copied. You can paste it into a social network or message.',
  'Não foi possível abrir o menu de partilha.': 'Could not open the share menu.',
  'Imagem do perfil criada.': 'Profile image created.',
  'Não foi possível criar a imagem do perfil.': 'Could not create the profile image.',
  'O teu dispositivo não suporta partilha de imagens; a imagem foi descarregada.': 'Your device does not support image sharing; the image was downloaded.',
  'A criar imagem…': 'Creating image…',
  'Copiar ligação': 'Copy link',
  // Authentication, registration and recovery validation messages
  'O nome de utilizador ou email é obrigatório': 'Username or email is required',
  'Introduza um email válido': 'Enter a valid email',
  'O nome de utilizador é demasiado longo': 'Username is too long',
  'Muitas tentativas falhadas. Conta bloqueada por 15 minutos.': 'Too many failed attempts. Account locked for 15 minutes.',
  'Por favor, corrija os erros no formulário antes de continuar.': 'Please correct the form errors before continuing.',
  'Erro no login:': 'Login error:',
  'Conta suspensa: <motivo>': 'Account suspended: <reason>',
  'Demasiadas tentativas. Tente novamente mais tarde.': 'Too many attempts. Try again later.',
  'Dados inválidos. Verifique as informações inseridas.': 'Invalid data. Check the information entered.',
  'Erro no servidor. Tente novamente mais tarde.': 'Server error. Try again later.',
  'Erro de ligação. Verifique a sua ligação à internet e tente novamente.': 'Connection error. Check your internet connection and try again.',
  'Erro ao iniciar sessão. Tente novamente mais tarde.': 'Could not log in. Try again later.',
  'à Globe Memories': 'to Globe Memories',
  'O login com Google ainda não está configurado no servidor.': 'Google login is not configured on the server yet.',
  'Não foi possível validar a conta Google.': 'Could not validate the Google account.',
  'Não foi possível iniciar sessão com Google. Tente novamente.': 'Could not log in with Google. Try again.',
  'Por favor, insira o seu nome de utilizador ou email.': 'Enter your username or email.',
  'Enviámos um PIN de recuperação para o email associado à conta. Verifique também o spam.': 'We sent a recovery PIN to the email associated with the account. Check your spam folder too.',
  'Foram feitas demasiadas tentativas. Tente novamente mais tarde.': 'Too many attempts were made. Try again later.',
  'Não encontrámos nenhuma conta com esse nome de utilizador ou email.': 'We could not find an account with that username or email.',
  'Não foi possível enviar o email agora. Tente novamente mais tarde.': 'Could not send the email now. Try again later.',
  'A palavra-passe deve conter pelo menos um carácter especial.': 'The password must contain at least one special character.',
  'Introduza o PIN de 6 dígitos recebido por email.': 'Enter the six-digit PIN received by email.',
  'Confirmação da palavra-passe é obrigatória.': 'Password confirmation is required.',
  'Palavra-passe alterada com sucesso!': 'Password changed successfully!',
  'Palavra-passe alterada com sucesso! Inicie sessão com a sua nova palavra-passe.': 'Password changed successfully! Log in with your new password.',
  'Código inválido ou expirado. Solicite um novo email de recuperação.': 'Invalid or expired code. Request a new recovery email.',
  'A enviar…': 'Sending…',
  'Enviar email': 'Send email',
  'Não foi possível gerar automaticamente um nome disponível. Escolha outro.': 'Could not automatically generate an available name. Choose another.',
  'Não foi possível confirmar o nome automático. Tente novamente ou escolha outro.': 'Could not confirm the automatic name. Try again or choose another.',
  'O nome de utilizador é obrigatório.': 'Username is required.',
  'O nome de utilizador deve ter 3–20 caracteres: letras, números, pontos ou underscore.': 'Username must be 3–20 characters: letters, numbers, dots or underscores.',
  'Este nome de utilizador já está a ser utilizado. Escolha outro.': 'This username is already in use. Choose another.',
  'Não foi possível verificar o nome de utilizador. Tente novamente.': 'Could not check the username. Try again.',
  'O email é obrigatório.': 'Email is required.',
  'O formato do email é inválido.': 'Email format is invalid.',
  'Este email já está registado. Tente iniciar sessão ou use outro.': 'This email is already registered. Log in or use another.',
  'Não foi possível verificar o email. Tente novamente.': 'Could not check the email. Try again.',
  'A confirmação da palavra-passe é obrigatória': 'Password confirmation is required',
  'As palavras-passe não coincidem': 'Passwords do not match',
  'O país é obrigatório': 'Country is required',
  'A cidade é obrigatória': 'City is required',
  'Tem de aceitar os Termos e Condições e a Política de Privacidade para continuar.': 'You must accept the Terms and Conditions and Privacy Policy to continue.',
  'Erro no registo:': 'Registration error:',
  'Registo realizado com sucesso! Bem-vindo!': 'Registration successful! Welcome!',
  'O email e o nome de utilizador já estão registados. Utilize dados diferentes.': 'The email and username are already registered. Use different details.',
  'Email já registado': 'Email already registered',
  'Nome de utilizador já existe': 'Username already exists',
  'Este email já está registado. Tente iniciar sessão ou use outro email.': 'This email is already registered. Log in or use another email.',
  'utilizador': 'user',
  'Este nome de utilizador já existe. Escolha outro.': 'This username already exists. Choose another.',
  'Dados já registados. Verifique o email e o nome de utilizador.': 'These details are already registered. Check the email and username.',
  'Erro ao registar. Tente novamente mais tarde.': 'Registration failed. Try again later.',
  'O registo com Google ainda não está configurado no servidor.': 'Google registration is not configured on the server yet.',
  'Não foi possível registar com Google. Tente novamente.': 'Could not register with Google. Try again.',
  'Selecione o país primeiro': 'Select the country first',
  'Preenchido automaticamente e disponível. Pode alterá-lo se quiser.': 'Filled automatically and available. You can change it if you wish.',
  '3–20 caracteres: letras, números, pontos ou underscore.': '3–20 characters: letters, numbers, dots or underscores.',
  'Um carácter especial': 'A special character',
  // Profile editor, settings and user-facing upload errors
  'Verifique os campos em falta antes de guardar.': 'Check the missing fields before saving.',
  'A capa não foi actualizada. Tente novamente.': 'The cover was not updated. Try again.',
  'A capa não foi removida. Tente novamente.': 'The cover was not removed. Try again.',
  'Perfil atualizado com sucesso!': 'Profile updated successfully!',
  'Erro ao guardar o perfil. Tente novamente.': 'Could not save the profile. Try again.',
  'Formato não suportado. Escolha uma imagem JPG, PNG, WEBP ou HEIC.': 'Unsupported format. Choose a JPG, PNG, WEBP or HEIC image.',
  'Um símbolo': 'A symbol',
  'O seu nome': 'Your name',
  'Sobre Mim': 'About me',
  'Conte um pouco sobre si e o tipo de viagens que gosta de fazer…': 'Tell us a little about yourself and the trips you enjoy…',
  // Weather page and forecast messages
  'Céu limpo': 'Clear sky',
  'Geada': 'Frost',
  'Chuva e neve': 'Rain and snow',
  'Chuva e neve forte': 'Heavy rain and snow',
  'Nevoeiro com geada': 'Fog with frost',
  'Grãos de neve': 'Snow grains',
  'Trovoada com granizo': 'Thunderstorm with hail',
  'Ver mais 5 dias': 'See 5 more days',
  'viagens passadas': 'past trips',
  'Nome da cidade contém caracteres não permitidos!': 'The city name contains disallowed characters!',
  'Este navegador não suporta geolocalização. A mostrar Lisboa por agora; pesquisa uma cidade manualmente.': 'This browser does not support geolocation. Showing Lisbon for now; search for a city manually.',
  'A localização devolvida pelo dispositivo é inválida. A mostrar Lisboa por agora; pesquisa uma cidade manualmente.': 'The location returned by the device is invalid. Showing Lisbon for now; search for a city manually.',
  'Erro de geolocalização:': 'Geolocation error:',
  'Erro ao obter coordenadas:': 'Error getting coordinates:',
  'Erro ao obter coordenadas da cidade. Tente novamente.': 'Could not get the city coordinates. Try again.',
  'Por favor, insira um nome de cidade válido.': 'Enter a valid city name.',
  'Data futura inválida.': 'Invalid future date.',
  'Data de viagem inválida.': 'Invalid trip date.',
  'Gateway meteorológico indisponível; a tentar fonte direta.': 'Weather gateway unavailable; trying the direct source.',
  'Dados meteorológicos inválidos recebidos da API.': 'Invalid weather data received from the API.',
  'Previsão para os próximos 15 dias': 'Forecast for the next 15 days',
  'Previsão para os próximos 10 dias': 'Forecast for the next 10 days',
  '🌤️ Temperatura amena — ideal para atividades ao ar livre.': '🌤️ Mild temperature — ideal for outdoor activities.',
  '🌦️ Possibilidade de chuva — convém um agasalho impermeável.': '🌦️ Chance of rain — a waterproof layer is recommended.',
  '💨 Vento forte — atenção a atividades no exterior.': '💨 Strong wind — take care with outdoor activities.',
  'Sensação térmica': 'Feels like',
  'Precipitação': 'Precipitation',
  'Previsão horária · Hoje': 'Hourly forecast · Today',
  'ver detalhes': 'view details',
  // Shared image/file validation errors
  'Não foi possível ler esta imagem.': 'Could not read this image.',
  'O navegador não conseguiu preparar a imagem.': 'The browser could not prepare the image.',
  'A conversão HEIC não devolveu uma imagem.': 'HEIC conversion did not return an image.',
  'Não foi possível converter esta foto HEIC. Tenta exportá-la como JPG e seleciona-a novamente.': 'Could not convert this HEIC photo. Export it as JPG and select it again.',
  'A imagem excede o limite de 20 MB. Escolha uma imagem mais pequena.': 'The image exceeds the 20 MB limit. Choose a smaller image.',
  // Achievements catalogue and profile editor language options
  'Ver 5 viagens de outros viajantes': 'View 5 trips from other travellers',
  'Publicar 5 viagens': 'Publish 5 trips',
  'Viajante Estelar': 'Star Traveller',
  'Comentar em 3 viagens de outros utilizadores': 'Comment on 3 trips from other users',
  'Viajante Organizado': 'Organised Traveller',
  'Ter 10 seguidores': 'Have 10 followers',
  'Mestre das Viagens': 'Trip Master',
  'Publicar 20 viagens': 'Publish 20 trips',
  'Influenciador de Viagens': 'Travel Influencer',
  'Receber 50 gostos no total nas suas viagens': 'Receive 50 total likes on your trips',
  'Comentar em 10 viagens de outros utilizadores': 'Comment on 10 trips from other users',
  'Visitar 3 cidades com altitude superior a 1000 metros': 'Visit 3 cities above 1,000 metres',
  'Viajante Frequente': 'Frequent Traveller',
  'Viajante Maratonista': 'Marathon Traveller',
  'Viajante apaixonada por cultura.': 'Traveller passionate about culture.',
  'Francês': 'French',
  'Alemão': 'German',
  'Holandês': 'Dutch',
  'Japonês': 'Japanese',
  'Árabe': 'Arabic',
  'Norueguês': 'Norwegian',
  'Dinamarquês': 'Danish',
  'Finlandês': 'Finnish',
  'Catalão': 'Catalan',
  'Húngaro': 'Hungarian',
  'Use 8+ caracteres, maiúscula, minúscula, número e símbolo.': 'Use 8+ characters, uppercase, lowercase, number and symbol.',
  // FAQ fragments split by inline links/strong elements
  ' no menu principal. Preenche os campos solicitados — nome, email e palavra-passe — e clica em \\"': ' in the main menu. Fill in the requested fields — name, email and password — and click \\"',
  '. Vais receber um email de confirmação para validar a tua conta antes de fazeres login.': '. You will receive a confirmation email to validate your account before logging in.',
  ' no menu principal, insere o teu email e palavra-passe. Se esqueceste a palavra-passe, há um link \\"': ' in the main menu, enter your email and password. If you forgot your password, there is a link \\"',
  ' que te guia na recuperação via email.': ' that guides you through email recovery.',
  ' no menu. Aqui podes atualizar a fotografia, descrição, nacionalidade e outras preferências. Podes também escolher o nível de privacidade (privado ou público) e quem vê as tuas viagens.': ' in the menu. You can update your photo, description, nationality and other preferences. You can also choose privacy and who can see your trips.',
  '. Preenche destino, datas, descrição, fotos/vídeos e tipo de transporte. No fim, clica em \\"': '. Fill in the destination, dates, description, photos/videos and transport. Finally, click \\"',
  'Abre a viagem desejada e clica em \\"': 'Open the desired trip and click \\"',
  '. Podes adicionar descrição, fotos, vídeos, data e localização GPS. Cada memória fica georreferenciada, criando um mapa pessoal das tuas aventuras.': '. You can add a description, photos, videos, date and GPS location. Each story is geotagged, creating a personal map of your adventures.',
  'Se o teu perfil for público, outros utilizadores visitam-no através de \\"': 'If your profile is public, other users can visit it through \\"',
  '. No perfil público, veem as tuas viagens, descrição e memórias destacadas.': '. On the public profile, they can see your trips, description and featured stories.',
  'São troféus digitais que desbloqueias ao atingir marcos — número de países, memórias, sequência de dias ativos, etc. Vê todas as tuas conquistas em \\"': 'These are digital trophies unlocked at milestones — countries, stories, active-day streaks and more. See all your achievements in \\"',
  ' no teu perfil.': ' on your profile.',
  'Na secção \\"': 'In the \\" section',
  ', podes criar e organizar viagens que tens em mente — destinos, datas, lista de itens, informações do local, clima previsto. Quando completares, moves para \\"': ', you can create and organise trips you have in mind — destinations, dates, packing lists, place information and forecast weather. When complete, move them to \\"',
  '. Aqui defines se o perfil é público ou privado, quem vê as tuas viagens, quem comenta, notificações, e podes bloquear utilizadores específicos.': '. Here you choose whether the profile is public or private, who sees your trips, who can comment and which users to block.',
  'Mensagem enviada! Responderemos em breve.': 'Message sent! We will reply soon.',
  // Community, forum and map controls
  'Selecione uma categoria!': 'Select a category!',
  'Todos os utilizadores podem ver, responder e gostar.': 'All users can view, reply and like.',
  'A procurar…': 'Searching…',
  'Fechar filtro de estilo': 'Close style filter',
  'Carregar mais': 'Load more',
  'A carregar comunidade...': 'Loading community…',
  'Erro ao desbloquear:': 'Error unblocking:',
  'Por favor, selecione pelo menos um motivo.': 'Please select at least one reason.',
  'Viajante denunciado com sucesso!': 'Traveller reported successfully!',
  'Viajante bloqueado com sucesso!': 'Traveller blocked successfully!',
  'Ocultar respostas': 'Hide replies',
  'Mostrar menos': 'Show less',
  // Weather condition labels and location errors
  'Nevoeiro': 'Fog',
  'Chuva fraca': 'Light rain',
  'Chuva moderada': 'Moderate rain',
  'Chuva forte': 'Heavy rain',
  'Chuva congelada fraca': 'Light freezing rain',
  'Chuva congelada moderada': 'Moderate freezing rain',
  'Chuva congelada forte': 'Heavy freezing rain',
  'Trovoada leve': 'Light thunderstorm',
  'Trovoada intensa': 'Severe thunderstorm',
  '⚠️ Alta probabilidade de chuva': '⚠️ High chance of rain',
  '🌧️ Probabilidade elevada de chuva — leve guarda-chuva.': '🌧️ High chance of rain — bring an umbrella.',
  'trovoada': 'thunderstorm',
  '⛈️ Risco de trovoada — evite zonas expostas.': '⛈️ Thunderstorm risk — avoid exposed areas.',
  'Vento': 'Wind',
  'A localização foi bloqueada. Pesquisa uma cidade manualmente ou permite o acesso à localização e tenta novamente.': 'Location access was blocked. Search for a city manually or allow location access and try again.',
  'Não foi possível determinar a tua localização. Pesquisa uma cidade manualmente ou tenta novamente.': 'Could not determine your location. Search for a city manually or try again.',
  'A localização demorou demasiado tempo. Pesquisa uma cidade manualmente ou tenta novamente.': 'Location lookup took too long. Search for a city manually or try again.',
  'Não foi possível obter a tua localização. Pesquisa uma cidade manualmente ou tenta novamente.': 'Could not get your location. Search for a city manually or try again.',
  // Trip validation and page titles
  'Informações Gerais': 'General information',
  'Fotos & Vídeos': 'Photos & Videos',
  'Nome da Viagem é obrigatório.': 'Trip name is required.',
  'Descrição Curta é obrigatória.': 'Short description is required.',
  'Descrição Longa é obrigatória.': 'Full description is required.',
  'Avaliação da viagem é obrigatória (escolha 1-5 estrelas).': 'Trip rating is required (choose 1–5 stars).',
  'Avaliação tem de estar entre 0 e 5.': 'Rating must be between 0 and 5.',
  'Data de Início é obrigatória.': 'Start date is required.',
  'Data de Início tem de estar no formato AAAA-MM-DD.': 'Start date must use the YYYY-MM-DD format.',
  'Data de Fim é obrigatória.': 'End date is required.',
  'Data de Fim tem de estar no formato AAAA-MM-DD.': 'End date must use the YYYY-MM-DD format.',
  'Data de início tem de ser anterior à data de fim.': 'Start date must be before the end date.',
  'País é obrigatório.': 'Country is required.',
  'Cidade é obrigatória.': 'City is required.',
  'Selecione pelo menos uma categoria.': 'Select at least one category.',
  'Selecione pelo menos uma língua.': 'Select at least one language.',
  'Nome da estadia é obrigatório.': 'Accommodation name is required.',
  'Descrição é obrigatória.': 'Description is required.',
  'Número de noites tem de ser >= 0.': 'Number of nights must be >= 0.',
  'Nome do ponto de referência é obrigatório.': 'Point of interest name is required.',
  'Nome da recomendação é obrigatório.': 'Recommendation name is required.',
  'Número do dia é obrigatório.': 'Day number is required.',
  'Imagem': 'Image',
  'Mapa de viagens': 'Trip map',
  'Fórum de viajantes': 'Traveller forum',
  'Enviar feedback': 'Send feedback',
  'Planear viagem': 'Plan a trip',
  'Alterar palavra-passe': 'Change password',
  'Email': 'Email',
  'email': 'email',
  'o-teu-email@exemplo.com': 'your-email@example.com',
  'Praça': 'Square',
  'Viagem multidestino: confirma o custo total — distribui os custos pelos destinos nas tabs Alojamento / Transportes / Comida.': 'Multi-destination trip: confirm the total cost — distribute costs across the Accommodation / Transport / Food tabs.',
  'Viagem multidestino: cada alojamento tem de ter um destino atribuído (tab Alojamento).': 'Multi-destination trip: each accommodation entry must have a destination assigned (Accommodation tab).',
  'Viagem multidestino: cada comida tem de ter um destino atribuído (tab Alimentação).': 'Multi-destination trip: each food entry must have a destination assigned (Food tab).',
  'Viagem multidestino: cada transporte tem de ter um destino atribuído (tab Transportes).': 'Multi-destination trip: each transport entry must have a destination assigned (Transport tab).',
  'Viagem multidestino: cada ponto negativo tem de ter pelo menos um destino atribuído (tab Extras).': 'Multi-destination trip: each drawback must have at least one destination assigned (Extras tab).',
  'Viagem multidestino: cada ponto de referência deve ter um destino válido ou ficar “Ao longo do percurso” (tab Locais).': 'Multi-destination trip: each point of interest must have a valid destination or be marked “Along the route” (Places tab).',
  'Viagem multidestino: cada dia do itinerário tem de ter um destino atribuído (tab Itinerário).': 'Multi-destination trip: each itinerary day must have a destination assigned (Itinerary tab).',
  'vídeo': 'video',
  'imagem': 'image',
  'o vídeo': 'the video',
  'a imagem': 'the image',
  'foto': 'photo',
  'Não foi possível publicar': 'Could not publish',
  'não foi possível publicar': 'could not publish',
  'Imagens, descrições ou linguagem ofensiva.': 'Offensive images, descriptions or language.',
  'Perfis, viagens ou locais inventados.': 'Invented profiles, trips or places.',
  'Comportamento agressivo ou ameaçador.': 'Aggressive or threatening behaviour.',
  'Ameaças, doxxing ou intimidação.': 'Threats, doxxing or intimidation.',
  'Descreva o motivo abaixo.': 'Describe the reason below.',
  'Locais inexistentes, preços manipulados.': 'Non-existent places or manipulated prices.',
  'Assédio/Abuso': 'Harassment/Abuse',
  'Plágio': 'Plagiarism',
  'Fotos ou textos copiados sem créditos.': 'Photos or text copied without credit.',
  'Quebra dos termos da plataforma.': 'Breach of the platform terms.',
};

// Keep audited UI additions synchronized with the other language catalogs.
Object.assign(PT_TO_EN, EN_UI_TRANSLATIONS);

const PREFIX_TRANSLATIONS = [
  [/^Perfil de (.+) \| Globe Memories$/, 'Profile of $1 | Globe Memories'],
  [/^(hoje|ontem),\s*(.+)$/i, (match, day, time) => `${day.toLowerCase() === 'hoje' ? 'today' : 'yesterday'}, ${time}`],
  [/^(\d{1,2}) de (jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)\. de (\d{4})$/i, (match, day, month, year) => {
    const months = { jan: 'Jan', fev: 'Feb', mar: 'Mar', abr: 'Apr', mai: 'May', jun: 'Jun', jul: 'Jul', ago: 'Aug', set: 'Sep', out: 'Oct', nov: 'Nov', dez: 'Dec' };
    return `${day} ${months[month.toLowerCase()]} ${year}`;
  }],
  [/^([0-9]+) viagens públicas no mapa$/, '$1 public trips on the map'],
  [/^([0-9]+) viagens?$/, '$1 trips'],
  [/^([0-9]+) comentários?$/, '$1 comments'],
  [/^([0-9]+) seguidores?$/, '$1 followers'],
  [/^([0-9]+) países visitados$/, '$1 countries visited'],
  [/^([0-9]+) cidades visitadas$/, '$1 cities visited'],
  [/^Dia ([0-9]+)$/, 'Day $1'],
  [/^([0-9]+) dias?$/, (match, count) => `${count} ${Number(count) === 1 ? 'day' : 'days'}`],
  [/^([0-9]+) visita[s]?$/, '$1 visits'],
  [/^Previsão de (.+) a (.+)$/, 'Forecast from $1 to $2'],
  [/^Destino ([0-9]+): seleciona um país para continuar\.$/, 'Destination $1: select a country to continue.'],
  [/^Não encontrámos nada para "(.+)"\.$/, 'Nothing found for "$1".'],
  [/^Ir para a secção (.+)$/, 'Go to section $1'],
  [/^A palavra-passe deve conter pelo menos (.+)$/, 'Password must contain at least $1'],
  [/^O custo total não pode exceder (.+)$/, 'Total cost cannot exceed $1'],
  [/^Usa o formulário "Fala Connosco" abaixo ou envia email para (.+)\. Respondemos em 24 a 48 horas\.$/, 'Use the “Contact us” form below or email $1. We reply within 24 to 48 hours.'],
  [/^(.+) começou a seguir-te$/, '$1 started following you'],
  [/^(.+) pediu para te seguir$/, '$1 requested to follow you'],
  [/^(.+) aceitou o teu pedido para seguir$/, '$1 accepted your follow request'],
  [/^(.+) respondeu à tua pergunta(.*)$/, '$1 replied to your question$2'],
  [/^(.+) respondeu ao teu comentário(.*)$/, '$1 replied to your comment$2'],
  [/^(.+) quer identificar-te na viagem "(.+)"$/, '$1 wants to identify you on the trip "$2"'],
  [/^(.+) aceitou a identificação na tua viagem "(.+)"$/, '$1 accepted the identification on your trip "$2"'],
  [/^há (.+)$/, '$1 ago'],
  [/^Erro ao (.+)$/, 'Error $1'],
  [/^Não foi possível (.+)$/, 'Could not $1'],
];

const EN_TO_PT = Object.fromEntries(Object.entries(PT_TO_EN).map(([pt, en]) => [en, pt]));
const PT_PLACE_TO_EN = Object.fromEntries(
  [...Object.entries(COUNTRY_PT), ...Object.entries(CITY_PT)].map(([en, pt]) => [pt, en]),
);
const normalizeTranslationKey = (value) => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();

// The spoken-language catalogue is stored in Portuguese with ISO codes in
// the API. Use the browser's CLDR language names for those labels wherever
// the catalogue is rendered (wizard, profile, trip details, etc.).
const SPOKEN_LANGUAGE_CODES_BY_NAME = Object.freeze({
  'português': 'pt', inglês: 'en', espanhol: 'es', francês: 'fr', alemão: 'de',
  italiano: 'it', holandês: 'nl', mandarim: 'zh', japonês: 'ja', árabe: 'ar',
  russo: 'ru', turco: 'tr', basco: 'eu', catalão: 'ca', galego: 'gl', grego: 'el',
  hebraico: 'he', hindi: 'hi', bengali: 'bn', urdu: 'ur', punjabi: 'pa', gujarati: 'gu',
  marata: 'mr', tâmil: 'ta', telugu: 'te', nepali: 'ne', cingalês: 'si', tailandês: 'th',
  vietnamita: 'vi', coreano: 'ko', indonésio: 'id', malaio: 'ms', filipino: 'tl',
  javanês: 'jv', sundanês: 'su', sueco: 'sv', norueguês: 'no', dinamarquês: 'da',
  finlandês: 'fi', islandês: 'is', polaco: 'pl', checo: 'cs', eslovaco: 'sk', húngaro: 'hu',
  romeno: 'ro', búlgaro: 'bg', sérvio: 'sr', croata: 'hr', bósnio: 'bs', esloveno: 'sl',
  ucraniano: 'uk', bielorrusso: 'be', lituano: 'lt', letão: 'lv', estónio: 'et',
  macedónio: 'mk', albanês: 'sq', arménio: 'hy', georgiano: 'ka', persa: 'fa', pastó: 'ps',
  azerbaijano: 'az', cazaque: 'kk', uzbeque: 'uz', turcomeno: 'tk', quirguiz: 'ky',
  tajique: 'tg', suaíli: 'sw', amárico: 'am', iorubá: 'yo', igbo: 'ig', hauçá: 'ha',
  somali: 'so', africâner: 'af', zulu: 'zu', xhosa: 'xh', quiniaruanda: 'rw',
  malgaxe: 'mg', maori: 'mi', havaiano: 'haw', galês: 'cy', irlandês: 'ga', maltês: 'mt',
  latim: 'la', esperanto: 'eo',
  // Older catalogue rows and imported trips can contain English display names.
  afrikaans: 'af', albanian: 'sq', amharic: 'am', armenian: 'hy', azerbaijani: 'az',
  basque: 'eu', bengali: 'bn', belarusian: 'be', bosnian: 'bs', bulgarian: 'bg',
  catalan: 'ca', kazakh: 'kk', czech: 'cs', sinhala: 'si', korean: 'ko', croatian: 'hr',
  danish: 'da', slovak: 'sk', slovenian: 'sl', spanish: 'es', estonian: 'et', filipino: 'tl',
  finnish: 'fi', french: 'fr', galician: 'gl', welsh: 'cy', georgian: 'ka', greek: 'el',
  hausa: 'ha', hawaiian: 'haw', hebrew: 'he', dutch: 'nl', hungarian: 'hu', igbo: 'ig',
  indonesian: 'id', english: 'en', yoruba: 'yo', irish: 'ga', icelandic: 'is', italian: 'it',
  japanese: 'ja', javanese: 'jv', latin: 'la', latvian: 'lv', lithuanian: 'lt',
  macedonian: 'mk', malay: 'ms', malagasy: 'mg', maltese: 'mt', mandarin: 'zh',
  marathi: 'mr', nepali: 'ne', norwegian: 'no', pashto: 'ps', persian: 'fa', polish: 'pl',
  portuguese: 'pt', punjabi: 'pa', kinyarwanda: 'rw', kyrgyz: 'ky', romanian: 'ro',
  russian: 'ru', serbian: 'sr', somali: 'so', swahili: 'sw', sundanese: 'su', swedish: 'sv',
  tajik: 'tg', tamil: 'ta', telugu: 'te', thai: 'th', turkmen: 'tk', turkish: 'tr',
  ukrainian: 'uk', urdu: 'ur', uzbek: 'uz', vietnamese: 'vi', xhosa: 'xh', zulu: 'zu',
});

const SPOKEN_LANGUAGE_CODES = new Set(Object.values(SPOKEN_LANGUAGE_CODES_BY_NAME));
const SPOKEN_LANGUAGE_ENGLISH_NAMES = Object.freeze(Object.fromEntries(
  Object.entries(SPOKEN_LANGUAGE_CODES_BY_NAME)
    .filter(([name]) => /^[a-z]+$/.test(name))
    .map(([name, code]) => [code, name.charAt(0).toLocaleUpperCase('en') + name.slice(1)]),
));

/**
 * Resolve API catalogue entries by their ISO code first, even when an older
 * or partially translated response has the code itself in `name` (for example
 * `{ code: "be", name: "be" }`). Intl.DisplayNames then localizes every
 * supported catalogue language consistently for the active UI locale.
 */
export function getLocalizedSpokenLanguageName(entry, language = 'en') {
  const name = typeof entry === 'string'
    ? entry.trim()
    : String(entry?.name || entry?.label || '').trim();
  const candidates = [
    entry?.code,
    entry?.languageCode,
    entry?.isoCode,
    typeof entry?.id === 'string' ? entry.id : null,
    name,
  ];
  let code = null;
  for (const candidate of candidates) {
    const normalized = String(candidate || '').trim().toLocaleLowerCase('en');
    if (!normalized) continue;
    const baseCode = normalized.split(/[-_]/, 1)[0];
    if (SPOKEN_LANGUAGE_CODES.has(baseCode)) {
      code = baseCode;
      break;
    }
    const namedCode = SPOKEN_LANGUAGE_CODES_BY_NAME[normalizeTranslationKey(normalized)];
    if (namedCode) {
      code = namedCode;
      break;
    }
  }
  if (!code) return null;

  const requestedLocale = SUPPORTED_LANGUAGE_CODES.includes(language) ? language : 'en';
  if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
    for (const locale of [requestedLocale, 'en']) {
      try {
        const localized = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
        if (localized && localized.toLocaleLowerCase('en') !== code) return localized;
      } catch (_) {
        // Fall through to the stable English catalogue name below.
      }
    }
  }
  return SPOKEN_LANGUAGE_ENGLISH_NAMES[code] || null;
}

function translateSpokenLanguageName(value, language) {
  const code = SPOKEN_LANGUAGE_CODES_BY_NAME[normalizeTranslationKey(value)];
  if (!code) return null;
  // Some Android WebViews return the input ISO code when their ICU data does
  // not contain a display name for a language. Reuse the defensive resolver:
  // it rejects that raw code and falls back to a readable English name.
  return getLocalizedSpokenLanguageName({ code }, language);
}

const translationLookup = Object.fromEntries(
  Object.entries(PT_TO_EN).map(([key, value]) => [normalizeTranslationKey(key), value])
);

const localeLookups = Object.fromEntries(
  Object.entries(PT_TO_LOCALE).map(([language, translations]) => [
    language,
    Object.fromEntries(Object.entries(translations).map(([key, value]) => [normalizeTranslationKey(key), value])),
  ]),
);

const dynamicTranslations = {
  es: {
    day: 'Día', daySingular: 'día', trips: 'viajes', publicTrips: 'viajes públicos en el mapa', comments: 'comentarios', followers: 'seguidores', countries: 'países visitados', cities: 'ciudades visitadas', days: 'días', visits: 'visitas', ago: 'hace', error: 'Error al', couldNot: 'No se pudo',
  },
  fr: {
    day: 'Jour', daySingular: 'jour', trips: 'voyages', publicTrips: 'voyages publics sur la carte', comments: 'commentaires', followers: 'abonnés', countries: 'pays visités', cities: 'villes visitées', days: 'jours', visits: 'visites', ago: 'il y a', error: 'Erreur lors de', couldNot: 'Impossible de',
  },
  de: {
    day: 'Tag', daySingular: 'Tag', trips: 'Reisen', publicTrips: 'öffentliche Reisen auf der Karte', comments: 'Kommentare', followers: 'Follower', countries: 'besuchte Länder', cities: 'besuchte Städte', days: 'Tage', visits: 'Besuche', ago: 'vor', error: 'Fehler beim', couldNot: 'Konnte nicht',
  },
  zh: {
    day: '第$1天', daySingular: '天', trips: '次旅行', publicTrips: '地图上的公开旅行', comments: '条评论', followers: '位粉丝', countries: '个访问过的国家', cities: '个访问过的城市', days: '天', visits: '次访问', ago: '$1前', error: '错误：', couldNot: '无法',
  },
  it: {
    day: 'Giorno', daySingular: 'giorno', trips: 'viaggi', publicTrips: 'viaggi pubblici sulla mappa', comments: 'commenti', followers: 'follower', countries: 'paesi visitati', cities: 'città visitate', days: 'giorni', visits: 'visite', ago: 'fa', error: 'Errore nel', couldNot: 'Impossibile',
  },
};

const DYNAMIC_NOTIFICATION_PATTERNS = [
  [/^(.+) começou a seguir-te$/i, {
    en: '$1 started following you', es: '$1 empezó a seguirte', fr: '$1 a commencé à vous suivre',
    de: '$1 folgt dir jetzt', zh: '$1开始关注你', it: '$1 ha iniziato a seguirti',
  }],
  [/^(.+) pediu para te seguir$/i, {
    en: '$1 requested to follow you', es: '$1 solicitó seguirte', fr: '$1 a demandé à vous suivre',
    de: '$1 hat angefragt, dir zu folgen', zh: '$1请求关注你', it: '$1 ha chiesto di seguirti',
  }],
  [/^(.+) aceitou o teu pedido para seguir$/i, {
    en: '$1 accepted your follow request', es: '$1 aceptó tu solicitud para seguirte', fr: '$1 a accepté votre demande d’abonnement',
    de: '$1 hat deine Folgeanfrage angenommen', zh: '$1接受了你的关注请求', it: '$1 ha accettato la tua richiesta di seguirlo',
  }],
  [/^(.+) respondeu à tua pergunta(.*)$/i, {
    en: '$1 replied to your question$2', es: '$1 respondió a tu pregunta$2', fr: '$1 a répondu à votre question$2',
    de: '$1 hat auf deine Frage geantwortet$2', zh: '$1回复了你的问题$2', it: '$1 ha risposto alla tua domanda$2',
  }],
  [/^(.+) respondeu ao teu comentário(.*)$/i, {
    en: '$1 replied to your comment$2', es: '$1 respondió a tu comentario$2', fr: '$1 a répondu à votre commentaire$2',
    de: '$1 hat auf deinen Kommentar geantwortet$2', zh: '$1回复了你的评论$2', it: '$1 ha risposto al tuo commento$2',
  }],
  [/^(.+) quer identificar-te na viagem "(.+)"$/i, {
    en: '$1 wants to identify you on the trip "$2"', es: '$1 quiere identificarte en el viaje "$2"',
    fr: '$1 souhaite vous identifier sur le voyage "$2"', de: '$1 möchte dich auf der Reise "$2" markieren',
    zh: '$1想在旅行“$2”中标记你', it: '$1 vuole identificarti nel viaggio "$2"',
  }],
  [/^(.+) aceitou a identificação na tua viagem "(.+)"$/i, {
    en: '$1 accepted the identification on your trip "$2"', es: '$1 aceptó la identificación en tu viaje "$2"',
    fr: '$1 a accepté l’identification sur votre voyage "$2"', de: '$1 hat die Markierung auf deiner Reise "$2" angenommen',
    zh: '$1接受了在你的旅行“$2”中标记对方', it: '$1 ha accettato l’identificazione nel tuo viaggio "$2"',
  }],
];

const DYNAMIC_MESSAGE_PATTERNS = [
  [/^Previsão de (.+) a (.+)$/i, {
    en: 'Forecast from $1 to $2', es: 'Previsión del $1 al $2', fr: 'Prévisions du $1 au $2',
    de: 'Vorhersage vom $1 bis zum $2', zh: '$1 至 $2 的天气预报', it: 'Previsioni dal $1 al $2',
  }],
  [/^Destino ([0-9]+): seleciona um país para continuar\.$/i, {
    en: 'Destination $1: select a country to continue.', es: 'Destino $1: selecciona un país para continuar.',
    fr: 'Destination $1 : sélectionnez un pays pour continuer.', de: 'Ziel $1: Wähle ein Land aus, um fortzufahren.',
    zh: '目的地 $1：请选择一个国家以继续。', it: 'Destinazione $1: seleziona un paese per continuare.',
  }],
  [/^Não encontrámos nada para "(.+)"\.$/i, {
    en: 'Nothing found for "$1".', es: 'No encontramos nada para "$1".', fr: 'Aucun résultat pour « $1 ».',
    de: 'Für „$1“ wurde nichts gefunden.', zh: '没有找到与“$1”相关的内容。', it: 'Nessun risultato per "$1".',
  }],
  [/^Ir para a secção (.+)$/i, {
    en: 'Go to section $1', es: 'Ir a la sección $1', fr: 'Accéder à la section $1',
    de: 'Zu Abschnitt $1', zh: '前往$1部分', it: 'Vai alla sezione $1',
  }],
  [/^A palavra-passe deve conter pelo menos (.+)$/i, {
    en: 'Password must contain at least $1', es: 'La contraseña debe incluir al menos $1',
    fr: 'Le mot de passe doit contenir au moins $1', de: 'Das Passwort muss mindestens $1 enthalten',
    zh: '密码至少应包含$1', it: 'La password deve contenere almeno $1',
  }],
  [/^O custo total não pode exceder (.+)$/i, {
    en: 'Total cost cannot exceed $1', es: 'El coste total no puede superar $1',
    fr: 'Le coût total ne peut pas dépasser $1', de: 'Die Gesamtkosten dürfen $1 nicht überschreiten',
    zh: '总费用不能超过$1', it: 'Il costo totale non può superare $1',
  }],
];

const interpolatePatternMatch = (template, match) => template.replace(/\$(\d)/g, (_, index) => match[Number(index)] || '');

function translateDynamicText(core, language) {
  let match = core.match(/^([0-9]+) visita[s]?$/i);
  if (match) {
    const count = Number(match[1]);
    const suffixes = {
      en: count === 1 ? 'visit' : 'visits',
      es: count === 1 ? 'visita' : 'visitas',
      fr: count === 1 ? 'visite' : 'visites',
      de: count === 1 ? 'Besuch' : 'Besuche',
      zh: '次访问',
      it: count === 1 ? 'visita' : 'visite',
    };
    return language === 'zh' ? `${match[1]}${suffixes.zh}` : `${match[1]} ${suffixes[language] || 'visits'}`;
  }

  match = core.match(/^Perfil de (.+) \| Globe Memories$/i);
  if (match) {
    const titles = {
      en: `Profile of ${match[1]} | Globe Memories`,
      es: `Perfil de ${match[1]} | Globe Memories`,
      fr: `Profil de ${match[1]} | Globe Memories`,
      de: `Profil von ${match[1]} | Globe Memories`,
      zh: `${match[1]}的个人资料 | Globe Memories`,
      it: `Profilo di ${match[1]} | Globe Memories`,
    };
    return titles[language] || core;
  }

  match = core.match(/^(hoje|ontem),\s*(.+)$/i);
  if (match) {
    const days = {
      en: match[1].toLowerCase() === 'hoje' ? 'Today' : 'Yesterday',
      es: match[1].toLowerCase() === 'hoje' ? 'Hoy' : 'Ayer',
      fr: match[1].toLowerCase() === 'hoje' ? 'Aujourd’hui' : 'Hier',
      de: match[1].toLowerCase() === 'hoje' ? 'Heute' : 'Gestern',
      zh: match[1].toLowerCase() === 'hoje' ? '今天' : '昨天',
      it: match[1].toLowerCase() === 'hoje' ? 'Oggi' : 'Ieri',
    };
    return `${days[language] || days.en}${language === 'zh' ? '，' : ', '}${match[2]}`;
  }

  for (const [pattern, translations] of DYNAMIC_NOTIFICATION_PATTERNS) {
    match = core.match(pattern);
    if (match) return interpolatePatternMatch(translations[language] || translations.en, match);
  }

  for (const [pattern, translations] of DYNAMIC_MESSAGE_PATTERNS) {
    match = core.match(pattern);
    if (match) return interpolatePatternMatch(translations[language] || translations.en, match);
  }

  match = core.match(/^Página ([0-9]+) de ([0-9]+)$/i);
  if (match) {
    const [, page, total] = match;
    const formats = {
      en: `Page ${page} of ${total}`,
      es: `Página ${page} de ${total}`,
      fr: `Page ${page} sur ${total}`,
      de: `Seite ${page} von ${total}`,
      zh: `第 ${page} 页，共 ${total} 页`,
      it: `Pagina ${page} di ${total}`,
    };
    return formats[language] || core;
  }

  match = core.match(/^([0-9]+) das tuas viagens$/i);
  if (match) {
    const summaries = {
      en: `${match[1]} of your trips`,
      es: `${match[1]} de tus viajes`,
      fr: `${match[1]} de vos voyages`,
      de: `${match[1]} deiner Reisen`,
      zh: `你的${match[1]}次旅行`,
      it: `${match[1]} dei tuoi viaggi`,
    };
    return summaries[language] || core;
  }

  match = core.match(/^([0-9]+) da comunidade$/i);
  if (match) {
    const summaries = {
      en: `${match[1]} from the community`,
      es: `${match[1]} de la comunidad`,
      fr: `${match[1]} de la communauté`,
      de: `${match[1]} aus der Community`,
      zh: `来自社区的${match[1]}条旅行`,
      it: `${match[1]} dalla community`,
    };
    return summaries[language] || core;
  }

  const labels = dynamicTranslations[language];
  if (!labels) return null;
  match = core.match(/^Usa o formulário "Fala Connosco" abaixo ou envia email para (.+)\. Respondemos em 24 a 48 horas\.$/i);
  if (match) {
    const email = match[1];
    const contactMessages = {
      en: `Use the “Contact us” form below or email ${email}. We usually reply within 24 to 48 hours.`,
      es: `Utiliza el formulario «Contáctanos» de abajo o escribe a ${email}. Solemos responder en un plazo de 24 a 48 horas.`,
      fr: `Utilisez le formulaire « Nous contacter » ci-dessous ou écrivez à ${email}. Nous répondons généralement sous 24 à 48 heures.`,
      de: `Nutze das untenstehende Formular „Kontakt“ oder schreibe an ${email}. Wir antworten in der Regel innerhalb von 24 bis 48 Stunden.`,
      zh: `请使用下方的“联系我们”表单，或发送邮件至 ${email}。我们通常会在 24 至 48 小时内回复。`,
      it: `Usa il modulo “Contattaci” qui sotto oppure scrivi a ${email}. Di solito rispondiamo entro 24–48 ore.`,
    };
    return contactMessages[language] || null;
  }
  match = core.match(/^Dia (\d+)$/i);
  if (match) return language === 'zh' ? labels.day.replace('$1', match[1]) : `${labels.day} ${match[1]}`;
  match = core.match(/^([0-9]+) viagens públicas no mapa$/i);
  if (match) return language === 'zh' ? `${match[1]} ${labels.publicTrips}` : `${match[1]} ${labels.publicTrips}`;
  match = core.match(/^([0-9]+) viagens?$/i);
  if (match) return language === 'zh' ? `${match[1]} ${labels.trips}` : `${match[1]} ${labels.trips}`;
  match = core.match(/^([0-9]+) comentários?$/i);
  if (match) return `${match[1]} ${labels.comments}`;
  match = core.match(/^([0-9]+) seguidores?$/i);
  if (match) return `${match[1]} ${labels.followers}`;
  match = core.match(/^([0-9]+) países visitados$/i);
  if (match) return `${match[1]} ${labels.countries}`;
  match = core.match(/^([0-9]+) cidades visitadas$/i);
  if (match) return `${match[1]} ${labels.cities}`;
  match = core.match(/^([0-9]+) dias?$/i);
  if (match) {
    const unit = Number(match[1]) === 1 ? labels.daySingular : labels.days;
    return `${match[1]} ${unit}`;
  }
  match = core.match(/^há (.+)$/i);
  if (match) return language === 'zh' ? labels.ago.replace('$1', match[1]) : `${labels.ago} ${match[1]}`;
  match = core.match(/^Erro ao (.+)$/i);
  if (match) return `${labels.error} ${match[1]}`;
  match = core.match(/^Não foi possível (.+)$/i);
  if (match) return language === 'zh' ? `${labels.couldNot}${match[1]}` : `${labels.couldNot} ${match[1]}`;
  return null;
}

function translateText(value, language) {
  if (language === 'pt' || typeof value !== 'string' || !value.trim()) return value;
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const core = value.slice(leading.length, value.length - trailing.length || undefined);
  const translations = language === 'en' ? PT_TO_EN : PT_TO_LOCALE[language];
  const lookup = language === 'en' ? translationLookup : localeLookups[language];
  if (translations?.[core]) return `${leading}${translations[core]}${trailing}`;
  const exact = lookup?.[normalizeTranslationKey(core)];
  if (exact) return `${leading}${exact}${trailing}`;
  const place = PT_PLACE_TO_EN[core];
  if (place) return `${leading}${language === 'en' ? place : place}${trailing}`;
  if (core.includes(',')) {
    const parts = core.split(',').map((part) => part.trim());
    const translatedParts = parts.map((part) => PT_PLACE_TO_EN[part] || part);
    if (translatedParts.some((part, index) => part !== parts[index])) {
      return `${leading}${translatedParts.join(', ')}${trailing}`;
    }
  }
  // JSX paragraphs are often split across lines for readability. Match a
  // whitespace-normalised form as well, while preserving the original outer
  // whitespace in the DOM.
  const normalisedCore = core.replace(/\s+/g, ' ').trim();
  if (normalisedCore !== core) {
    const normalisedTranslation = translations?.[normalisedCore]
      || lookup?.[normalizeTranslationKey(normalisedCore)];
    if (normalisedTranslation) return `${leading}${normalisedTranslation}${trailing}`;
  }
  const spokenLanguage = translateSpokenLanguageName(core, language);
  if (spokenLanguage) return `${leading}${spokenLanguage}${trailing}`;
  const dynamic = translateDynamicText(core, language);
  if (dynamic) return `${leading}${dynamic}${trailing}`;
  if (language !== 'en') {
    const english = PT_TO_EN[core] || translationLookup[normalizeTranslationKey(core)];
    if (english) return `${leading}${english}${trailing}`;
  }
  for (const [pattern, replacement] of PREFIX_TRANSLATIONS) {
    if (language === 'en' && pattern.test(core)) return `${leading}${core.replace(pattern, replacement)}${trailing}`;
  }
  return value;
}

const NO_TRANSLATE_SELECTOR = 'script, style, textarea, [data-gm-no-translate]';

function isTranslatableTextNode(node, excludedSelector = NO_TRANSLATE_SELECTOR) {
  if (!node?.parentElement) return false;
  return !node.parentElement.closest(excludedSelector);
}

export function translateDocument(root, language, textNodeStates, attrOriginals, additionalExcludedSelector = '') {
  if (!root) return;
  const excludedSelector = additionalExcludedSelector
    ? `${NO_TRANSLATE_SELECTOR}, ${additionalExcludedSelector}`
    : NO_TRANSLATE_SELECTOR;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }
  textNodes.forEach((textNode) => {
    if (!isTranslatableTextNode(textNode, excludedSelector)) return;
    const current = textNode.nodeValue || '';
    const previous = textNodeStates.get(textNode);
    // React can update a text node in place (for example, a live likes or
    // comments count). Keep the original only while the DOM still contains
    // the translated value we last wrote; otherwise the current value is a
    // fresh React render and becomes the new source text. Storing only the
    // first value made the observer restore stale counters after mutations.
    const original = previous && current === previous.translated
      ? previous.original
      : current;
    const next = language === 'pt' ? original : translateText(original, language);
    if (current !== next) textNode.nodeValue = next;
    textNodeStates.set(textNode, { original, translated: next });
  });

  root.querySelectorAll?.('[placeholder], [aria-label], [title], [alt], option').forEach((element) => {
    if (element.closest(excludedSelector)) return;
    const attrs = attrOriginals.get(element) || {};
    ['placeholder', 'aria-label', 'title', 'alt'].forEach((attribute) => {
      if (!element.hasAttribute(attribute)) return;
      if (!(attribute in attrs)) attrs[attribute] = element.getAttribute(attribute);
      const original = attrs[attribute];
      element.setAttribute(attribute, language === 'pt' ? original : translateText(original, language));
    });
    if (element.tagName === 'OPTION') {
      if (!('text' in attrs)) attrs.text = element.textContent;
      element.textContent = language === 'pt' ? attrs.text : translateText(attrs.text, language);
    }
    attrOriginals.set(element, attrs);
  });
}

const LanguageContext = createContext({
  language: 'pt',
  setLanguage: () => {},
  t: (value) => value,
});

export const LanguageProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const { pathname } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
  const isLandingRoute = pathname === '/' || pathname === '/landing';
  const nativeAndroid = isNativeAndroid();
  const browserLanguage = detectBrowserLanguage();
  const storedManualLanguage = readLocalLanguage(MANUAL_LANGUAGE_STORAGE_KEY);
  // Automatic locale inference stays on this device. A language selected in
  // Settings is explicit and must win over browser/keyboard signals.
  const manualLanguageRef = useRef(Boolean(storedManualLanguage));
  const automaticLanguageRef = useRef(!storedManualLanguage && (nativeAndroid || Boolean(browserLanguage)));
  const [language, setLanguageState] = useState(() => {
    return resolveInitialLanguage({
      manualLanguage: storedManualLanguage,
      browserLanguage,
      storedLanguage: readLocalLanguage('gm:language'),
      accountLanguage: user?.language,
    });
  });
  const currentLanguageRef = useRef(language);
  const textNodeStatesRef = useRef(new WeakMap());
  const attrOriginalsRef = useRef(new WeakMap());

  const setLanguage = useCallback((nextLanguage, { source = 'manual' } = {}) => {
    const next = SUPPORTED_LANGUAGE_CODES.includes(nextLanguage) ? nextLanguage : 'pt';
    if (source === 'automatic' && manualLanguageRef.current) return;
    if (source === 'manual') {
      manualLanguageRef.current = true;
      automaticLanguageRef.current = false;
      try { window.localStorage.setItem(MANUAL_LANGUAGE_STORAGE_KEY, next); } catch (_) { /* no-op */ }
    } else if (source === 'automatic') {
      automaticLanguageRef.current = true;
    }
    currentLanguageRef.current = next;
    setLanguageState(next);
    try { window.localStorage.setItem('gm:language', next); } catch (_) { /* no-op */ }
    try { window.dispatchEvent(new CustomEvent('gm:language-changed', { detail: { language: next } })); } catch (_) { /* no-op */ }
  }, []);

  // The account language remains the fallback when the browser does not expose
  // one of our supported locales. Detected device/keyboard languages are local
  // to this installation and intentionally do not overwrite the account.
  useEffect(() => {
    if (!user?.language || manualLanguageRef.current || automaticLanguageRef.current) return;
    setLanguage(user.language, { source: 'account' });
  }, [user?.id, user?.language, setLanguage]);

  // PWAs and browsers expose preferred browser/device locales, not the live
  // virtual-keyboard layout. Recheck on focus so a system locale change is
  // reflected without making an account-wide language change.
  useEffect(() => {
    if (nativeAndroid || isAdminRoute) return undefined;
    const syncBrowserLanguage = () => {
      if (manualLanguageRef.current || document.visibilityState === 'hidden') return;
      const detected = detectBrowserLanguage();
      if (!detected) return;
      automaticLanguageRef.current = true;
      if (currentLanguageRef.current !== detected) setLanguage(detected, { source: 'automatic' });
    };
    syncBrowserLanguage();
    window.addEventListener('focus', syncBrowserLanguage);
    document.addEventListener('visibilitychange', syncBrowserLanguage);
    return () => {
      window.removeEventListener('focus', syncBrowserLanguage);
      document.removeEventListener('visibilitychange', syncBrowserLanguage);
    };
  }, [isAdminRoute, nativeAndroid, setLanguage]);

  // The Android shell sends the active IME subtype through this local event.
  // Native Android can read the selected keyboard layout; the web/PWA cannot.
  useEffect(() => {
    if (!nativeAndroid || isAdminRoute) return undefined;
    const applyKeyboardLanguage = (event) => {
      if (manualLanguageRef.current) return;
      const detected = resolveSupportedLanguage(event?.detail?.language);
      if (!detected) return;
      automaticLanguageRef.current = true;
      if (currentLanguageRef.current !== detected) setLanguage(detected, { source: 'automatic' });
    };
    window.addEventListener('gm:keyboard-language', applyKeyboardLanguage);
    if (window.__gmKeyboardLanguage) {
      applyKeyboardLanguage({ detail: { language: window.__gmKeyboardLanguage } });
    }
    return () => window.removeEventListener('gm:keyboard-language', applyKeyboardLanguage);
  }, [isAdminRoute, nativeAndroid, setLanguage]);

  // Keep already-authenticated devices in sync with the account preference.
  // A lightweight owner-only endpoint is checked periodically and when the
  // tab becomes active, so changing language on one device updates other
  // open sessions without requiring another login. Storage events also keep
  // tabs in the same browser in sync immediately.
  useEffect(() => {
    if (!user?.id || isAdminRoute) return undefined;
    const userId = user.id;
    let disposed = false;
    let requestInFlight = false;

    const syncAccountLanguage = async () => {
      if (disposed || requestInFlight || document.visibilityState === 'hidden') return;
      requestInFlight = true;
      try {
        const { data } = await api.get(`/users/${userId}/language`, { noReadCache: true });
        const accountLanguage = data?.language;
        if (disposed || !SUPPORTED_LANGUAGE_CODES.includes(accountLanguage)) return;

        if (!manualLanguageRef.current && !automaticLanguageRef.current && currentLanguageRef.current !== accountLanguage) {
          setLanguage(accountLanguage, { source: 'account' });
        }
        setUser((currentUser) => {
          if (!currentUser || String(currentUser.id) !== String(userId)
            || currentUser.language === accountLanguage) return currentUser;
          const nextUser = { ...currentUser, language: accountLanguage };
          persistStoredUser(nextUser);
          return nextUser;
        });
      } catch (_) {
        // A temporary network failure must not interrupt the current session
        // or reset the local language; the next focus/poll retries.
      } finally {
        requestInFlight = false;
      }
    };

    const syncFromStorage = (event) => {
      if (event.key === MANUAL_LANGUAGE_STORAGE_KEY && SUPPORTED_LANGUAGE_CODES.includes(event.newValue)) {
        manualLanguageRef.current = true;
        automaticLanguageRef.current = false;
        if (currentLanguageRef.current !== event.newValue) {
          setLanguage(event.newValue, { source: 'storage' });
        }
        return;
      }
      if (event.key !== 'gm:language' || !SUPPORTED_LANGUAGE_CODES.includes(event.newValue)) return;
      const manual = readLocalLanguage(MANUAL_LANGUAGE_STORAGE_KEY);
      if (manual && manual !== event.newValue) return;
      if (manual) manualLanguageRef.current = true;
      if (currentLanguageRef.current !== event.newValue) setLanguage(event.newValue, { source: 'storage' });
    };
    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') syncAccountLanguage();
    };

    syncAccountLanguage();
    const intervalId = window.setInterval(syncAccountLanguage, 15000);
    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', syncWhenVisible);
    document.addEventListener('visibilitychange', syncWhenVisible);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', syncWhenVisible);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [isAdminRoute, setLanguage, setUser, user?.id]);

  const t = useCallback((value) => translateText(value, language), [language]);

  useEffect(() => {
    // The backoffice is intentionally Portuguese-only. More importantly, the
    // DOM translation observer must not rewrite React-managed values there:
    // doing so can restore stale text (for example, dashboard counts to 0)
    // when React updates an existing text node with the loaded API result.
    if (isAdminRoute) {
      document.documentElement.lang = 'pt-PT';
      return undefined;
    }

    if (!isLandingRoute) {
      document.documentElement.lang = language === 'zh' ? 'zh-CN' : language === 'pt' ? 'pt-PT' : language;
    }
    const apply = () => translateDocument(
      document.body,
      language,
      textNodeStatesRef.current,
      attrOriginalsRef.current,
      '.gm-landing',
    );
    let frame = null;
    const requestFrame = window.requestAnimationFrame
      ? (callback) => window.requestAnimationFrame(callback)
      : (callback) => window.setTimeout(callback, 0);
    const cancelFrame = window.cancelAnimationFrame
      ? (handle) => window.cancelAnimationFrame(handle)
      : (handle) => window.clearTimeout(handle);
    const scheduleApply = () => {
      if (frame !== null) return;
      frame = requestFrame(() => {
        frame = null;
        apply();
      });
    };
    scheduleApply();
    const observer = new MutationObserver(scheduleApply);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      if (frame !== null) cancelFrame(frame);
    };
  }, [isAdminRoute, isLandingRoute, language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
export { PT_TO_EN, EN_TO_PT, translateText };
